import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Typography, Space, Button, Row, Col, Modal, Form, Select, InputNumber, Alert, Popconfirm, Collapse } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import ProposalService from '../../services/ProposalService';
import AttributeService from '../../services/AttributeService';
import AuthorizedPersonService from '../../services/AuthorizedPersonService';
import { openNotification, formatDate, TRANSPORTATION_TYPE_OPTIONS, toTurkishUpper, isAccountActive } from '../../utils';
import {
  getInspectionPlanByProposal,
  deleteInspectionAppointment,
  getInspectorAvailabilitySettings,
  fetchAvailableInspectorsForAttributes,
  checkInspectorAvailability,
} from '../../modules/inspection-appointments/services/inspection-appointmentService';
import { listPlanningReservationsByRange } from '../../modules/planning-reservations/services/planningReservationService';
import dayjs from 'dayjs';
import { listGroupsAll } from '../../modules/groups/services/groupService';
import { listPublicHolidaysAll } from '../../modules/public-holidays/services/public-holidayService';
// Map for showing address location
import { APP_DATE_FORMAT } from 'config/constants';
import ControlAddressCard from '../../components/ControlAddressCard';
import PlanAuthorizedPersonsCard from './components/PlanAuthorizedPersonsCard';
import CategorySummaryCard from './components/CategorySummaryCard';
import InspectorPlanningCard from './components/InspectorPlanningCard';
import ParameterCard from './components/ParameterCard';
import type { DayRange, TransportationAssignment, TransportationGroup, TransportationGroupEntry } from './components/types';
import type { PublicHoliday } from '../../modules/public-holidays/types/PublicHoliday';
import { buildBlockingPublicHolidayDateSet } from './publicHolidayUtils';
import { savePlanningDetail } from './planningDetailService';
import './PlanningDetail.scss';

export interface AttributeRequirementInfo {
  requiredProfessionIds: number[];
  requiredMeasuringInstrumentIds: number[];
}

export interface InspectorEligibilityInfo {
  professionIds?: Array<number | string | null | undefined>;
  professions?: Array<{ id?: number | string | null | undefined } | null | undefined>;
  measuringInstrumentIds?: Array<number | string | null | undefined>;
}

type OfferingKeyMaps = {
  fieldControlSummary: Record<string, number>;
  offeringLabelsByKey: Record<string, string>;
  offeringIdByKey: Record<string, number | null>;
  offeringKeyById: Record<string, string>;
};

const coerceToNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

export const extractNumericIds = (values?: unknown): number[] => {
  if (!values) {
    return [];
  }
  if (values instanceof Set) {
    return extractNumericIds(Array.from(values));
  }
  if (!Array.isArray(values)) {
    return [];
  }
  const collected: number[] = [];
  values.forEach((entry) => {
    if (entry == null) {
      return;
    }
    if (Array.isArray(entry)) {
      collected.push(...extractNumericIds(entry));
      return;
    }
    if (typeof entry === 'object') {
      const candidateId = (entry as { id?: unknown }).id;
      const coerced = coerceToNumber(candidateId);
      if (coerced !== null) {
        collected.push(coerced);
      }
      return;
    }
    const coerced = coerceToNumber(entry);
    if (coerced !== null) {
      collected.push(coerced);
    }
  });
  return Array.from(new Set(collected));
};

const areNumberArraysEqual = (a?: number[], b?: number[]): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  return a.every((val, idx) => val === b[idx]);
};

export const isInspectorEligibleForRequirement = (
  inspector: InspectorEligibilityInfo,
  requirement?: AttributeRequirementInfo | null,
): boolean => {
  if (!requirement) {
    return true;
  }
  const requiredProfessions = new Set(requirement.requiredProfessionIds || []);
  const requiredInstruments = new Set(requirement.requiredMeasuringInstrumentIds || []);

  const inspectorProfessionIds = new Set([
    ...extractNumericIds(inspector?.professionIds),
    ...extractNumericIds(inspector?.professions),
  ]);
  const inspectorInstrumentIds = new Set(extractNumericIds(inspector?.measuringInstrumentIds));

  const professionSatisfied =
    requiredProfessions.size === 0 ||
    Array.from(inspectorProfessionIds).some((id) => requiredProfessions.has(id));
  const instrumentSatisfied =
    requiredInstruments.size === 0 ||
    Array.from(inspectorInstrumentIds).some((id) => requiredInstruments.has(id));

  return professionSatisfied && instrumentSatisfied;
};

export const filterInspectorsByRequirementSets = (
  inspectors: InspectorEligibilityInfo[] = [],
  requirementSets: AttributeRequirementInfo[] = [],
): InspectorEligibilityInfo[] => {
  const activeRequirements = requirementSets.filter(
    (req) =>
      req && (req.requiredProfessionIds?.length || req.requiredMeasuringInstrumentIds?.length),
  );
  if (!activeRequirements.length) {
    return [...inspectors];
  }
  return inspectors.filter((inspector) =>
    activeRequirements.some((requirement) => isInspectorEligibleForRequirement(inspector, requirement)),
  );
};

const { Title, Text } = Typography;

interface PlanEntry {
  id: string;
  taskId?: number;
  appointmentId?: number;
  taskDate?: string;
  taskStatus?: string;
  offeringKey?: string;
  paramKey: string;
  paramName: string;
  paramQuantity?: number;
  inspectorId?: number;
  inspectorName: string;
  date: string;
  count: number;
}

type ParameterInspectorAllocation = {
  inspectorId: number;
  count: number;
};

interface AppointmentPayload {
  appointmentId?: number;
  inspectorId: number;
  startDate: string;
  endDate: string;
  status: string;
  ignoreAppointmentId?: number;
  transportation?: string;
  transportationType?: string;
  transportType?: string;
  note?: string;
  transportationNote?: string;
  offeringId?: number | null;
}

const normalizeTransportValue = (value?: string | null): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const normalizeNoteValue = (value?: string | null): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return undefined;
  }
  return toTurkishUpper(trimmed) ?? trimmed;
};

const OFFERING_KEY_DELIMITER = '::';

const buildOfferingKey = (offeringId?: number | string | null, offeringName?: string | null) => {
  const numericId = coerceToNumber(offeringId);
  const idPart = numericId != null ? String(numericId) : 'no';
  const label =
    typeof offeringName === 'string' && offeringName.trim().length > 0 ? offeringName.trim() : 'Diğer';
  return `${idPart}${OFFERING_KEY_DELIMITER}${label}`;
};

const parseOfferingKey = (key?: string | null): { id: number | null; name: string } => {
  if (!key) {
    return { id: null, name: 'Diğer' };
  }
  const [idPart, ...rest] = key.split(OFFERING_KEY_DELIMITER);
  const parsedId = idPart === 'no' ? null : coerceToNumber(idPart);
  const label = rest.length > 0 ? rest.join(OFFERING_KEY_DELIMITER) : 'Diğer';
  return {
    id: parsedId != null ? parsedId : null,
    name: label && label.trim().length > 0 ? label : 'Diğer',
  };
};

const normalizeLicenseList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((entry) => {
        if (typeof entry === 'string') {
          return entry.trim();
        }
        if (entry == null) {
          return '';
        }
        return String(entry).trim();
      })
      .filter((entry) => entry.length > 0 && entry.toUpperCase() !== 'NONE');
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0 && entry.toUpperCase() !== 'NONE');
  }
  return [];
};

const normalizeTextValue = (value: unknown): string => {
  if (typeof value === 'string') {
    return value.trim();
  }
  if (value == null) {
    return '';
  }
  return String(value).trim();
};

const DAILY_APPOINTMENT_DURATION_LIMIT_HOURS = 24;

const normalizePlanningBundle = (bundle: any) => {
  if (!bundle || typeof bundle !== 'object') {
    return bundle;
  }

  const normalizeEntry = (entry: any) => {
    if (!entry || typeof entry !== 'object') {
      return entry;
    }
    const accountId = Number(entry.accountId ?? entry.account?.id ?? entry.inspectorId ?? entry.inspector?.id);
    const normalizedId = Number.isNaN(accountId) ? undefined : accountId;
    return {
      ...entry,
      accountId: normalizedId,
      inspectorId: normalizedId ?? entry.inspectorId,
    };
  };

  const appointments = Array.isArray(bundle.appointments)
    ? bundle.appointments.map(normalizeEntry)
    : [];
  const tasks = Array.isArray(bundle.tasks)
    ? bundle.tasks.map(normalizeEntry)
    : [];

  return {
    ...bundle,
    appointments,
    tasks,
  };
};

const computeMergedAuthorizedPersons = (
  currentAuthorized: any,
  companyAuthorized: any,
  mergedIds: number[],
) => {
  const mapped = new Map<number, any>();
  const selectionSet = new Set(mergedIds);

  if (Array.isArray(currentAuthorized)) {
    currentAuthorized.forEach((person: any) => {
      const id = Number(person?.id);
      if (Number.isFinite(id)) {
        mapped.set(id, person);
      }
    });
  }

  if (Array.isArray(companyAuthorized)) {
    companyAuthorized.forEach((person: any) => {
      const id = Number(person?.id);
      if (Number.isFinite(id) && selectionSet.has(id)) {
        mapped.set(id, person);
      }
    });
  }

  return mergedIds
    .map((id) => mapped.get(id))
    .filter((person): person is any => Boolean(person));
};

const toNumericIdList = (input: any): number[] => {
  if (!Array.isArray(input)) {
    return [];
  }
  return input
    .map((value) => {
      const num = Number(value);
      return Number.isFinite(num) ? num : null;
    })
    .filter((value): value is number => value != null);
};

const areIdListsEqual = (left: number[], right: number[]): boolean => {
  const normalizedLeft = Array.from(new Set(left)).sort((a, b) => a - b);
  const normalizedRight = Array.from(new Set(right)).sort((a, b) => a - b);
  if (normalizedLeft.length !== normalizedRight.length) {
    return false;
  }
  return normalizedLeft.every((value, index) => value === normalizedRight[index]);
};

const areParameterInspectorAllocationsEqual = (
  left?: ParameterInspectorAllocation[],
  right?: ParameterInspectorAllocation[],
): boolean => {
  if (left === right) return true;
  const leftList = Array.isArray(left) ? left : [];
  const rightList = Array.isArray(right) ? right : [];
  if (leftList.length !== rightList.length) {
    return false;
  }
  return leftList.every((item, index) =>
    item.inspectorId === rightList[index]?.inspectorId &&
    item.count === rightList[index]?.count);
};

const normalizeParameterInspectorAllocations = (
  allocations: ParameterInspectorAllocation[],
  allowedInspectorIds: Set<number>,
  maxSelectableInspectors: number,
  quantity: number,
): ParameterInspectorAllocation[] => {
  const maxSelectable = Math.max(1, Math.floor(maxSelectableInspectors) || 1);
  const maxQuantity = Math.max(1, Math.floor(quantity) || 1);
  const unique = new Map<number, number>();

  (Array.isArray(allocations) ? allocations : []).forEach((entry) => {
    const inspectorId = Number(entry?.inspectorId);
    if (Number.isNaN(inspectorId) || inspectorId <= 0 || !allowedInspectorIds.has(inspectorId)) {
      return;
    }
    if (unique.has(inspectorId)) {
      return;
    }
    const rawCount = Number(entry?.count);
    const count = Number.isFinite(rawCount) && rawCount > 0 ? Math.floor(rawCount) : 1;
    unique.set(inspectorId, Math.max(1, Math.min(count, maxQuantity)));
  });

  const normalized = Array.from(unique.entries())
    .slice(0, maxSelectable)
    .map(([inspectorId, count]) => ({ inspectorId, count }));

  let total = normalized.reduce((acc, item) => acc + item.count, 0);
  if (total > maxQuantity) {
    let overflow = total - maxQuantity;
    for (let index = normalized.length - 1; index >= 0 && overflow > 0; index -= 1) {
      const reducible = normalized[index].count - 1;
      if (reducible <= 0) {
        continue;
      }
      const reduction = Math.min(reducible, overflow);
      normalized[index].count -= reduction;
      overflow -= reduction;
    }
    total = normalized.reduce((acc, item) => acc + item.count, 0);
    if (total > maxQuantity) {
      return normalized.slice(0, maxQuantity).map((item) => ({ ...item, count: 1 }));
    }
  }

  return normalized;
};

const splitQuantityAcrossInspectors = (
  inspectorIds: number[],
  quantity: number,
): ParameterInspectorAllocation[] => {
  const validIds = Array.from(
    new Set(
      (inspectorIds || [])
        .map((value) => Number(value))
        .filter((value) => !Number.isNaN(value) && value > 0),
    ),
  );
  if (validIds.length === 0) {
    return [];
  }

  const totalQuantity = Math.max(1, Math.floor(quantity) || 1);
  const base = Math.floor(totalQuantity / validIds.length);
  let remainder = totalQuantity % validIds.length;

  return validIds.map((inspectorId) => {
    const extra = remainder > 0 ? 1 : 0;
    if (remainder > 0) {
      remainder -= 1;
    }
    return {
      inspectorId,
      count: base + extra,
    };
  });
};

const PlanningDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(true);
  const [proposal, setProposal] = useState<any>(null);
  const [attributesMeta, setAttributesMeta] = useState<any[]>([]);
  const [authorizedPersons, setAuthorizedPersons] = useState<any[]>([]);
  const [inspectors, setInspectors] = useState<any[]>([]);
  const [allGroups, setAllGroups] = useState<any[]>([]);
  const [planModalOpen, setPlanModalOpen] = useState<boolean>(false);
  const [planTarget, setPlanTarget] = useState<any | null>(null);
  const [planForm] = Form.useForm();
  const [sumExceeds, setSumExceeds] = useState<boolean>(false);
  const [pagePlans, setPagePlans] = useState<any[]>([]);
  const [editingExisting, setEditingExisting] = useState<boolean>(false);
  const [planAuthorizedPersonIds, setPlanAuthorizedPersonIds] = useState<number[]>([]);
  const [savingPlanAuthorized, setSavingPlanAuthorized] = useState<boolean>(false);
  const [savingPlanStatus, setSavingPlanStatus] = useState<'PLANNED' | 'RESERVE' | null>(null);
  const savingPlanInFlight = useRef(false);
  const updatingAppointmentInFlight = useRef(false);
  const [updatingAppointment, setUpdatingAppointment] = useState(false);
  const deletingAppointmentInFlight = useRef(false);
  const [deletingAppointment, setDeletingAppointment] = useState(false);
  const [pendingDeletedAppointmentId, setPendingDeletedAppointmentId] = useState<number | null>(null);
  const [dailySelectedInspectorsModalOpen, setDailySelectedInspectorsModalOpen] = useState<boolean>(false);
  const [existingPlan, setExistingPlan] = useState<any | null>(null);
  const [initialPlanApplied, setInitialPlanApplied] = useState<boolean>(false);
  const [availabilityMode, setAvailabilityMode] = useState<'DAILY' | 'HOURLY'>('HOURLY');
  // Gün (day) inputları için override state (kullanıcı düzenleyebilir)
  const [fieldControlDays, setFieldControlDays] = useState<Record<string, number>>({});
  const [offeringDates, setOfferingDates] = useState<Record<string, DayRange[]>>({});
  const [offeringInspectors, setOfferingInspectors] = useState<Record<string, (number | undefined)[]>>({});
  const [offeringAppointmentIds, setOfferingAppointmentIds] = useState<Record<string, (number | undefined)[]>>({});
  const [availableInspectorIds, setAvailableInspectorIds] = useState<Record<string, Record<number, number[]>>>({});
  const [availabilityLoading, setAvailabilityLoading] = useState<Record<string, Record<number, boolean>>>({});
  const [offeringTransports, setOfferingTransports] = useState<Record<string, (string | undefined)[]>>({});
  const [offeringTransportNotes, setOfferingTransportNotes] = useState<Record<string, (string | undefined)[]>>({});
  const [preservedInspectorIdsByOffering, setPreservedInspectorIdsByOffering] = useState<Record<string, number[]>>({});
  const [transportForm] = Form.useForm();
  const [publicHolidays, setPublicHolidays] = useState<PublicHoliday[]>([]);
  const [parameterInspectorSelections, setParameterInspectorSelections] = useState<Record<number, ParameterInspectorAllocation[]>>({});

  const proposalStatusId = useMemo(() => {
    if (!proposal) {
      return null;
    }
    const direct = (proposal as any)?.statusId;
    if (typeof direct === 'number' && Number.isFinite(direct)) {
      return direct;
    }
    if (typeof direct === 'string') {
      const parsedDirect = Number(direct);
      if (Number.isFinite(parsedDirect)) {
        return parsedDirect;
      }
    }
    const nested = (proposal as any)?.status?.id;
    if (typeof nested === 'number' && Number.isFinite(nested)) {
      return nested;
    }
    if (typeof nested === 'string') {
      const parsedNested = Number(nested);
      if (Number.isFinite(parsedNested)) {
        return parsedNested;
      }
    }
    return null;
  }, [proposal]);

  const isReadOnly = useMemo(() => {
    if (proposalStatusId == null) {
      return true;
    }
    return (proposalStatusId !== 5 && proposalStatusId !== 6 && proposalStatusId !== 7);
  }, [proposalStatusId]);

  useEffect(() => {
    if (isReadOnly) {
      setPlanModalOpen(false);
    }
  }, [isReadOnly]);

  useEffect(() => {
    getInspectorAvailabilitySettings()
      .then((settings) => {
        if (settings?.mode === 'HOURLY') {
          setAvailabilityMode('HOURLY');
        } else {
          setAvailabilityMode('DAILY');
        }
      })
      .catch((error) => {
        console.warn('Failed to fetch inspector availability settings', error);
      });
  }, []);

  const handleFieldControlDayChange = (offering: string, value: number | null) => {
    if (isReadOnly) {
      return;
    }
    const numericValue = typeof value === 'number' ? value : Number(value ?? 0);
    const safeValue = Number.isFinite(numericValue) ? Math.max(0, Math.floor(numericValue)) : 0;

    setFieldControlDays((prev) => ({
      ...prev,
      [offering]: safeValue,
    }));

    setOfferingDates((prev) => {
      if (safeValue <= 0) {
        const { [offering]: _removed, ...rest } = prev;
        return rest;
      }
      const existing = prev[offering] ? [...prev[offering]] : ([] as DayRange[]);
      const next = existing.slice(0, safeValue);
      while (next.length < safeValue) {
        next.push([null, null]);
      }
      return {
        ...prev,
        [offering]: next,
      };
    });

    setOfferingInspectors((prev) => {
      if (safeValue <= 0) {
        const { [offering]: _removed, ...rest } = prev;
        return rest;
      }
      const existing = prev[offering] ? [...prev[offering]] : [];
      const next = existing.slice(0, safeValue);
      while (next.length < safeValue) {
        next.push(undefined);
      }
      return {
        ...prev,
        [offering]: next,
      };
    });

    setOfferingAppointmentIds((prev) => {
      if (safeValue <= 0) {
        const { [offering]: _removed, ...rest } = prev;
        return rest;
      }
      const existing = prev[offering] ? [...prev[offering]] : [];
      const next = existing.slice(0, safeValue);
      while (next.length < safeValue) {
        next.push(undefined);
      }
      return {
        ...prev,
        [offering]: next,
      };
    });

    setOfferingTransports((prev) => {
      if (safeValue <= 0) {
        const { [offering]: _removed, ...rest } = prev;
        return rest;
      }
      const existing = prev[offering] ? [...prev[offering]] : [];
      const next = existing.slice(0, safeValue);
      while (next.length < safeValue) {
        next.push(undefined);
      }
      return {
        ...prev,
        [offering]: next,
      };
    });

    setOfferingTransportNotes((prev) => {
      if (safeValue <= 0) {
        const { [offering]: _removed, ...rest } = prev;
        return rest;
      }
      const existing = prev[offering] ? [...prev[offering]] : [];
      const next = existing.slice(0, safeValue);
      while (next.length < safeValue) {
        next.push(undefined);
      }
      return {
        ...prev,
        [offering]: next,
      };
    });
  };

  const updateTransportationAssignments = (
    assignments: TransportationAssignment[],
    nextValue?: string,
  ) => {
    if (isReadOnly) {
      return;
    }
    const normalized = normalizeTransportValue(nextValue);
    setOfferingTransports((prev) => {
      const copy = { ...prev } as Record<string, (string | undefined)[]>;
      assignments.forEach(({ offering, index }) => {
        const list = copy[offering] ? [...copy[offering]] : [];
        while (list.length <= index) {
          list.push(undefined);
        }
        list[index] = normalized;
        copy[offering] = list;
      });
      return copy;
    });
  };

  const setAvailabilityLoadingFlag = useCallback((offering: string, index: number, value: boolean) => {
    setAvailabilityLoading((prev) => {
      const current = prev[offering];
      const nextForOffering = { ...(current || {}) } as Record<number, boolean>;
      if (value) {
        if (nextForOffering[index] === value) {
          return prev;
        }
        nextForOffering[index] = value;
      } else {
        if (!current || !(index in current)) {
          return prev;
        }
        delete nextForOffering[index];
      }
      const next = { ...prev } as Record<string, Record<number, boolean>>;
      if (Object.keys(nextForOffering).length > 0) {
        next[offering] = nextForOffering;
      } else if (offering in next) {
        delete next[offering];
      } else {
        return prev;
      }
      return next;
    });
  }, []);

  const setAvailableInspectorsForRange = useCallback(
    (offering: string, index: number, ids?: number[]) => {
      setAvailableInspectorIds((prev) => {
        const current = prev[offering];
        const nextForOffering = { ...(current || {}) } as Record<number, number[]>;
        if (ids === undefined) {
          if (!current || !(index in current)) {
            return prev;
          }
          delete nextForOffering[index];
        } else {
          const prevIds = current?.[index];
          if (areNumberArraysEqual(prevIds, ids)) {
            return prev;
          }
          nextForOffering[index] = ids;
        }
        const next = { ...prev } as Record<string, Record<number, number[]>>;
        if (Object.keys(nextForOffering).length > 0) {
          next[offering] = nextForOffering;
        } else if (offering in next) {
          delete next[offering];
        } else {
          return prev;
        }
        return next;
      });
    },
    [],
  );

  const updateTransportationNotes = (
    assignments: TransportationAssignment[],
    nextValue?: string,
  ) => {
    if (isReadOnly) {
      return;
    }
    const normalized = normalizeNoteValue(nextValue);
    setOfferingTransportNotes((prev) => {
      const copy = { ...prev } as Record<string, (string | undefined)[]>;
      assignments.forEach(({ offering, index }) => {
        const list = copy[offering] ? [...copy[offering]] : [];
        while (list.length <= index) {
          list.push(undefined);
        }
        list[index] = normalized;
        copy[offering] = list;
      });
      return copy;
    });
  };

  const goToCompanyEdit = () => {
    if (proposal?.company?.id) {
      navigate(`/companies/${proposal.company.id}?edit=true`);
    }
  };

  // Satır bazında maksimum adet hesaplayıcı
  const computeRowMax = (rowIndex: number) => {
    const maxQty = planTarget?.quantity || 0;
    // Düzenleme modunda mevcut liste toplamını double-count etmeyelim
    const alreadyPlanned = editingExisting
      ? 0
      : pagePlans
        .filter((p) => p.paramKey === planTarget?.key)
        .reduce((acc, cur) => acc + (Number(cur.count) || 0), 0);

    const vals = planForm.getFieldValue('plans') || [];
    const othersUsed = vals.reduce((acc: number, cur: any, idx: number) => {
      if (idx === rowIndex) return acc;
      return acc + (Number(cur?.count) || 0);
    }, 0);
    const remaining = maxQty - alreadyPlanned - othersUsed;
    return remaining > 0 ? remaining : 1;
  };

  useEffect(() => {
    if (!id) return;

    setInitialPlanApplied(false);
    setExistingPlan(null);
    setPagePlans([]);
    setOfferingDates({});
    setOfferingInspectors({});
    setPreservedInspectorIdsByOffering({});
    setFieldControlDays({});

    const fetchAll = async () => {
      try {
        const { data } = await ProposalService.get(Number(id));
        const normalizedPlanIds = toNumericIdList(data?.planAuthorizedPersonIds);
        const fallbackAuthorizedIds = toNumericIdList(data?.authorizedPersonIds);
        const effectivePlanIds = normalizedPlanIds.length ? normalizedPlanIds : fallbackAuthorizedIds;
        setProposal({
          ...data,
          planAuthorizedPersonIds: effectivePlanIds,
        });
        setPlanAuthorizedPersonIds(effectivePlanIds);

        // Fetch attribute meta for name/description mapping
        const attrRes = await AttributeService.getAll();
        setAttributesMeta(attrRes.data || []);

        // Fetch company authorized persons to resolve names from IDs
        if (data?.company?.id) {
          const authRes = await AuthorizedPersonService.getCompanyAuthorizedPersons(data.company.id);
          setAuthorizedPersons(authRes.data || []);
        }

        try {
          const planRes = await getInspectionPlanByProposal(Number(id));
          setExistingPlan(normalizePlanningBundle(planRes));
        } catch (planErr) {
          console.error('Plan detayları yüklenemedi:', planErr);
          setExistingPlan(null);
        }
      } catch (e) {
        console.error(e);
        openNotification('error', 'Hata', 'Detaylar yüklenemedi.');
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [id]);

  const handlePlanningContextInspectorsLoaded = useCallback((items: any[]) => {
    setInspectors(Array.isArray(items) ? items : []);
  }, []);

  // Fetch categories (groups) — default to only active unless explicitly requesting all
  useEffect(() => {
    listGroupsAll('isActive=true')
      .then((data: any) => setAllGroups(Array.isArray(data) ? data : []))
      .catch(() => setAllGroups([]));
  }, []);

  useEffect(() => {
    listPublicHolidaysAll()
      .then((data: PublicHoliday[] | undefined) => {
        if (Array.isArray(data)) {
          setPublicHolidays(data);
        } else {
          setPublicHolidays([]);
        }
      })
      .catch((error) => {
        console.warn('Failed to fetch public holidays', error);
        setPublicHolidays([]);
      });
  }, []);

  const publicHolidayDateSet = useMemo<Set<string>>(() => {
    return buildBlockingPublicHolidayDateSet(publicHolidays);
  }, [publicHolidays]);

  const attributesById = useMemo(() => {
    const map = new Map<number, any>();
    (attributesMeta || []).forEach((a: any) => map.set(a.id, a));
    return map;
  }, [attributesMeta]);

  const inspectorList = useMemo(() => (Array.isArray(inspectors) ? inspectors : []), [inspectors]);

  const selectedAuthorizedPeople = useMemo(() => {
    const ids: number[] = proposal?.authorizedPersonIds || [];
    return (authorizedPersons || []).filter((p: any) => ids.includes(p.id));
  }, [proposal, authorizedPersons]);

  const handleSavePlanAuthorizedPersons = async (silent = false): Promise<boolean> => {
    if (isReadOnly) {
      return true;
    }
    if (!proposal?.id) {
      if (!silent) {
        openNotification('error', 'Hata', 'Teklif bilgisine ulaşılamadı.');
      }
      return false;
    }

    const existingAuthorizedIds = toNumericIdList(proposal?.authorizedPersonIds);
    const selectedPlanAuthorizedIds = Array.from(new Set<number>(toNumericIdList(planAuthorizedPersonIds)));
    const planEligibleAuthorizedIds = new Set<number>(
      (authorizedPersons || [])
        .filter((person: any) => {
          const types = Array.isArray(person?.notificationTypes) ? person.notificationTypes : [];
          return types.includes('PLAN');
        })
        .map((person: any) => Number(person?.id))
        .filter((personId: number) => Number.isFinite(personId)),
    );
    const preservedAuthorizedIds = existingAuthorizedIds.filter(
      (personId) => !planEligibleAuthorizedIds.has(personId),
    );
    const mergedAuthorizedIds = Array.from(
      new Set<number>([...preservedAuthorizedIds, ...selectedPlanAuthorizedIds]),
    );

    if (areIdListsEqual(existingAuthorizedIds, mergedAuthorizedIds)) {
      return true;
    }

    try {
      setSavingPlanAuthorized(true);
      await ProposalService.updateAuthorizedPersons(proposal.id, {
        authorizedPersonIds: mergedAuthorizedIds,
      });
      setProposal((prev: any) =>
        prev
          ? {
              ...prev,
              planAuthorizedPersonIds: [...selectedPlanAuthorizedIds],
              authorizedPersonIds: mergedAuthorizedIds,
              authorizedPersons: computeMergedAuthorizedPersons(
                prev?.authorizedPersons,
                authorizedPersons,
                mergedAuthorizedIds,
              ),
            }
          : prev
      );
      if (!silent) {
        openNotification('success', 'Başarılı', 'Plan yetkilileri güncellendi.');
      }
      return true;
    } catch (e) {
      console.error('Plan yetkilileri kaydedilirken hata oluştu:', e);
      if (!silent) {
        openNotification('error', 'Hata', 'Plan yetkilileri kaydedilemedi.');
      }
      return false;
    } finally {
      setSavingPlanAuthorized(false);
    }
  };

  const handleSavePlan = async (status: 'PLANNED' | 'RESERVE' = 'PLANNED') => {
    if (isReadOnly || savingPlanInFlight.current || updatingAppointmentInFlight.current || deletingAppointmentInFlight.current || pendingDeletedAppointmentId != null) return;
    savingPlanInFlight.current = true;
    setSavingPlanStatus(status);
    try {
      await savePlan(status);
    } finally {
      savingPlanInFlight.current = false;
      setSavingPlanStatus(null);
    }
  };

  const savePlan = async (status: 'PLANNED' | 'RESERVE') => {
    if (isReadOnly) {
      return;
    }
    const proposalId = Number(proposal?.id);
    if (!proposalId) {
      openNotification('error', 'Hata', 'Teklif bilgisine ulaşılamadı.');
      return;
    }

    const planStatus = status;
    if (planStatus === 'PLANNED' && proposalStatusId === 6) {
      const company = proposal?.company ?? {};
      const companyAddresses = Array.isArray(company?.addresses) ? company.addresses : [];
      const billingAddress = companyAddresses.find(
        (address: any) => normalizeTextValue(address?.type).toUpperCase() === 'BILLING'
      ) ?? null;
      const companyAddress = companyAddresses.find(
        (address: any) => normalizeTextValue(address?.type).toUpperCase() === 'COMPANY'
      ) ?? billingAddress ?? companyAddresses[0] ?? null;
      const address = proposal?.address ?? companyAddress ?? {};
      const missingFields: string[] = [];

      if (!normalizeTextValue(company?.name ?? company?.title)) {
        missingFields.push('Unvan');
      }
      if (!normalizeTextValue(address?.addressLine1)) {
        missingFields.push('Adres');
      }
      if (!normalizeTextValue(address?.city) || !normalizeTextValue(address?.state ?? address?.district)) {
        missingFields.push('İl/İlçe');
      }
      if (!normalizeTextValue(address?.phone ?? company?.phoneNumber)) {
        missingFields.push('Telefon');
      }
      if (!normalizeTextValue(company?.email)) {
        missingFields.push('E-posta');
      }
      const isBillingAddressComplete =
        !!billingAddress &&
        !!normalizeTextValue(billingAddress?.addressLine1);
      if (!isBillingAddressComplete) {
        missingFields.push('Fatura Adresi');
      }
      if (!normalizeTextValue(company?.taxOffice)) {
        missingFields.push('Vergi Dairesi');
      }
      if (!normalizeTextValue(company?.taxNumber)) {
        missingFields.push('Vergi Numarası');
      }

      if (missingFields.length > 0) {
        openNotification(
          'warning',
          'Eksik Firma Bilgisi',
          `Planı tamamlamak için şu alanlar zorunludur: ${missingFields.join(', ')}`,
          5,
        );
        return;
      }
    }

    const authorizedSaved = await handleSavePlanAuthorizedPersons(true);
    if (!authorizedSaved) {
      openNotification('error', 'Hata', 'Yetkili kişiler kaydedilemedi.');
      return;
    }

    type SelectedPlanItem = {
      inspectorId: number;
      attributeId: number;
      qty: number;
      offeringKey?: string;
      dateLabel?: string;
      taskId?: number;
      appointmentId?: number;
      taskDate?: string;
      taskStatus?: string;
    };
    type CalendarSelectedPlanItem = SelectedPlanItem & {
      offeringKey: string;
      dateLabel: string;
    };
    type TaskPayloadItem = {
      inspectorId: number;
      attributeId: number;
      qty: number;
      date: string;
      appointmentDate: string;
      status: string;
      taskId?: number;
      appointmentId?: number;
    };

    const parameterCardPlans: SelectedPlanItem[] = (selectedParams || [])
      .flatMap((param: any) => {
        const attributeId = Number(param?.key);
        if (Number.isNaN(attributeId) || attributeId <= 0) {
          return [];
        }
        const allocations = parameterInspectorSelections?.[attributeId] || [];
        return allocations
          .map((allocation) => {
            const inspectorId = Number(allocation?.inspectorId);
            const count = Number(allocation?.count);
            if (
              Number.isNaN(inspectorId) ||
              inspectorId <= 0 ||
              Number.isNaN(count) ||
              count <= 0
            ) {
              return null;
            }
            return {
              inspectorId,
              attributeId,
              qty: Math.floor(count),
            };
          })
          .filter((item): item is SelectedPlanItem => Boolean(item));
      });

    const buildCalendarSlotKey = (offeringKey: string, inspectorId: number, dateLabel: string) =>
      `${offeringKey}__${inspectorId}__${dateLabel}`;

    const calendarSlotKeySet = new Set<string>();
    Object.entries(offeringDates || {}).forEach(([offeringKey, ranges]) => {
      const assignedInspectors = offeringInspectors?.[offeringKey] || [];
      (ranges || []).forEach((range, index) => {
        const start = range?.[0];
        if (!start || !dayjs.isDayjs(start) || !start.isValid()) {
          return;
        }
        const inspectorId = Number(assignedInspectors[index]);
        if (Number.isNaN(inspectorId) || inspectorId <= 0) {
          return;
        }
        calendarSlotKeySet.add(buildCalendarSlotKey(offeringKey, inspectorId, start.format('DD/MM/YYYY HH:mm')));
      });
    });

    const fallbackPagePlans: CalendarSelectedPlanItem[] = (pagePlans || [])
      .map((pl: any): CalendarSelectedPlanItem | null => {
        const inspectorId = Number(pl?.inspectorId);
        const attributeId = Number(pl?.paramKey ?? pl?.attributeId);
        const qty = Number(pl?.count);
        const meta = attributesById.get(attributeId) || {};
        const offeringKey = pl.offeringKey ?? buildOfferingKey(meta?.offering?.id, meta?.offering?.name);
        const dateLabel = typeof pl?.date === 'string' ? pl.date : '';
        const hasCalendarSlots = calendarSlotKeySet.size > 0;
        if (
          hasCalendarSlots &&
          (!dateLabel || !calendarSlotKeySet.has(buildCalendarSlotKey(offeringKey, inspectorId, dateLabel)))
        ) {
          return null;
        }
        if (
          Number.isNaN(inspectorId) ||
          inspectorId <= 0 ||
          Number.isNaN(attributeId) ||
          attributeId <= 0 ||
          Number.isNaN(qty) ||
          qty <= 0
        ) {
          return null;
        }
        return {
          inspectorId,
          attributeId,
          qty: Math.floor(qty),
          offeringKey,
          dateLabel,
          taskId: pl.taskId,
          appointmentId: pl.appointmentId,
          taskDate: pl.taskDate,
          taskStatus: pl.taskStatus,
        };
      })
      .filter((item): item is CalendarSelectedPlanItem => item !== null);
    // Retain individual persisted tasks even when the parameter card aggregates their quantities.
    const effectivePlans: SelectedPlanItem[] = parameterCardPlans.length > 0
      ? parameterCardPlans.flatMap((selection) => {
        let remaining = selection.qty;
        const result: SelectedPlanItem[] = [];
        fallbackPagePlans.filter((plan) => plan.taskId != null
          && plan.inspectorId === selection.inspectorId && plan.attributeId === selection.attributeId)
          .forEach((plan) => {
            const qty = Math.min(remaining, plan.qty);
            if (qty > 0) result.push({ ...plan, qty });
            remaining -= qty;
          });
        if (remaining > 0) result.push({ ...selection, qty: remaining });
        return result;
      })
      : fallbackPagePlans;

    if (planStatus === 'PLANNED') {
      const plannedTotals = new Map<string, number>();
      effectivePlans.forEach((item: SelectedPlanItem) => {
        const key = String(item?.attributeId ?? '');
        if (!key) {
          return;
        }
        const count = Number(item?.qty) || 0;
        plannedTotals.set(key, (plannedTotals.get(key) || 0) + count);
      });
      const mismatchedParams: string[] = [];
      (selectedParams || []).forEach((param: any) => {
        const key = String(param?.key ?? '');
        if (!key) {
          return;
        }
        const required = Number(param?.quantity) || 0;
        const planned = plannedTotals.get(key) || 0;
        if (planned !== required) {
          const name = (param?.name || `Parametre #${key}`).toString();
          mismatchedParams.push(`${name}: ${planned}/${required}`);
        }
      });
      if (mismatchedParams.length > 0) {
        openNotification(
          'error',
          'Eksik Planlama',
          `Planı tamamlamak için tüm parametreler eksiksiz planlanmalıdır. Eksik veya fazla kalanlar: ${mismatchedParams.join(' | ')}`,
        );
        return;
      }
    }

    const appointmentKeys = new Set<string>([
      ...Object.keys(offeringInspectors || {}),
      ...Object.keys(offeringDates || {}),
      ...Object.keys(fieldControlDays || {}),
    ]);

    const appointments: AppointmentPayload[] = [];
    const appointmentDetails: {
      appointmentId?: number;
      inspectorId: number;
      startDate: string;
      endDate: string;
      appointmentDate: string;
    }[] = [];
    let hasPartialAppointment = false;
    let hasInvalidOrder = false;
    let hasDurationExceed = false;

    appointmentKeys.forEach((key) => {
      const inspectorList = offeringInspectors?.[key] || [];
      const dateRangeList = offeringDates?.[key] || [];
      const maxLen = Math.max(inspectorList.length, dateRangeList.length);

      for (let idx = 0; idx < maxLen; idx += 1) {
        const inspectorRaw = inspectorList[idx];
        const inspectorId = Number(inspectorRaw);
        const range = dateRangeList[idx];
        const startRaw = range?.[0] ?? null;
        const endRaw = range?.[1] ?? null;
        const startDateValue = dayjs.isDayjs(startRaw) ? startRaw : startRaw ? dayjs(startRaw) : null;
        const endDateValue = dayjs.isDayjs(endRaw) ? endRaw : endRaw ? dayjs(endRaw) : null;
        const hasInspector = !Number.isNaN(inspectorId) && inspectorId > 0;
        const hasStart = !!(startDateValue && startDateValue.isValid());
        const hasEnd = !!(endDateValue && endDateValue.isValid());

        if (hasStart && hasEnd) {
          if (startDateValue.isAfter(endDateValue)) {
            hasPartialAppointment = true;
            hasInvalidOrder = true;
            continue;
          }

          const diffHours = endDateValue.diff(startDateValue, 'hour', true);
          if (availabilityMode === 'DAILY' && diffHours > DAILY_APPOINTMENT_DURATION_LIMIT_HOURS) {
            hasPartialAppointment = true;
            hasDurationExceed = true;
            continue;
          }
        }

        if (!hasInspector && !hasStart && !hasEnd) {
          continue;
        }
        if (!hasInspector || !hasStart || !hasEnd) {
          hasPartialAppointment = true;
          continue;
        }

        if (!hasStart || !hasEnd) {
          continue;
        }

        const startDateIso = startDateValue.toISOString();
        const endDateIso = endDateValue.toISOString();
        const appointmentStartDate = startDateValue.format('YYYY-MM-DD');
        const startDateWithTime = startDateValue.format('YYYY-MM-DD[T]HH:mm:ss');
        const endDateWithTime = endDateValue.format('YYYY-MM-DD[T]HH:mm:ss');
        const transportList = offeringTransports?.[key] || [];
        const noteList = offeringTransportNotes?.[key] || [];
        const transportationValue = normalizeTransportValue(transportList[idx]);
        const noteValue = normalizeNoteValue(noteList[idx]);
        const appointmentPayload: AppointmentPayload = {
          inspectorId,
          startDate: startDateWithTime,
          endDate: endDateWithTime,
          status: planStatus,
        };

        if (transportationValue) {
          appointmentPayload.transportation = transportationValue;
          appointmentPayload.transportationType = transportationValue;
          appointmentPayload.transportType = transportationValue;
        }

        if (noteValue) {
          appointmentPayload.note = noteValue;
          appointmentPayload.transportationNote = noteValue;
        }

        const mapValue = offeringIdByKey?.[key];
        const parsedKeyMeta = parseOfferingKey(key);
        const resolvedOfferingId =
          mapValue != null && !Number.isNaN(Number(mapValue))
            ? Number(mapValue)
            : parsedKeyMeta.id != null && !Number.isNaN(parsedKeyMeta.id)
              ? parsedKeyMeta.id
              : null;
        if (resolvedOfferingId != null && !Number.isNaN(resolvedOfferingId)) {
          appointmentPayload.offeringId = resolvedOfferingId;
        }

        const ignoreAppointmentIdRaw = offeringAppointmentIds?.[key]?.[idx];
        const ignoreAppointmentId = Number(ignoreAppointmentIdRaw);
        if (!Number.isNaN(ignoreAppointmentId) && ignoreAppointmentId > 0) {
          appointmentPayload.ignoreAppointmentId = ignoreAppointmentId;
          appointmentPayload.appointmentId = ignoreAppointmentId;
        }

        appointments.push(appointmentPayload);
        appointmentDetails.push({
          appointmentId: appointmentPayload.appointmentId,
          inspectorId,
          startDate: startDateIso,
          endDate: endDateIso,
          appointmentDate: appointmentStartDate,
        });
      }
    });

    if (hasInvalidOrder) {
      openNotification('error', 'Hata', 'Haftalık plan tablosunda başlangıç tarihi bitiş tarihinden sonra olamaz.');
      return;
    }

    if (hasDurationExceed) {
      openNotification('error', 'Hata', 'Haftalık plan tablosundaki tarih aralığı 24 saati aşamaz.');
      return;
    }

    if (hasPartialAppointment) {
      openNotification(
        'error',
        'Hata',
        'Haftalık plan tablosundaki her satır için başlangıç ve bitiş tarihleri ile P.K.M Uzmanı seçiniz.',
      );
      return;
    }

    if (appointments.length === 0) {
      openNotification('error', 'Hata', 'En az bir planlama tarihi aralığı ve uzmanı seçiniz.');
      return;
    }

    const seenInspectorDay = new Set<string>();
    const duplicateInspectorDay = new Set<string>();
    appointments.forEach((appointment) => {
      const inspectorId = Number(appointment?.inspectorId);
      if (Number.isNaN(inspectorId) || inspectorId <= 0) {
        return;
      }
      const startMoment = dayjs(appointment?.startDate);
      if (!startMoment.isValid()) {
        return;
      }
      const dayKey = startMoment.format('YYYY-MM-DD');
      const compositeKey = `${inspectorId}|${dayKey}`;
      if (seenInspectorDay.has(compositeKey)) {
        duplicateInspectorDay.add(compositeKey);
      } else {
        seenInspectorDay.add(compositeKey);
      }
    });
    if (duplicateInspectorDay.size > 0) {
      const duplicateSummary = Array.from(duplicateInspectorDay)
        .map((entry) => {
          const [idPart, dayKey] = entry.split('|');
          const inspectorId = Number(idPart);
          const inspector = inspectorById.get(inspectorId) || {};
          const firstName = (inspector?.account?.firstName || '').toString();
          const lastName = (inspector?.account?.lastName || '').toString();
          const inspectorName = `${firstName} ${lastName}`.trim() || `#${inspectorId}`;
          const formattedDay = dayjs(dayKey, 'YYYY-MM-DD', true).isValid()
            ? dayjs(dayKey, 'YYYY-MM-DD', true).format('DD/MM/YYYY')
            : dayKey;
          return `${inspectorName} (${formattedDay})`;
        })
        .join(', ');
      openNotification(
        'error',
        'Hata',
        `Aynı teklifte aynı güne aynı P.K.M Uzmanı yalnızca bir kez planlanabilir. Çakışan seçimler: ${duplicateSummary}`,
      );
      return;
    }

    const appointmentInspectorIds = new Set<number>();
    appointments.forEach((appointment) => {
      const inspectorId = Number(appointment?.inspectorId);
      if (!Number.isNaN(inspectorId) && inspectorId > 0) {
        appointmentInspectorIds.add(inspectorId);
      }
    });

    const parameterInspectorIds = new Set<number>();
    effectivePlans.forEach((plan) => {
      const inspectorId = Number(plan?.inspectorId);
      if (!Number.isNaN(inspectorId) && inspectorId > 0) {
        parameterInspectorIds.add(inspectorId);
      }
    });

    const missingParameterInspectorIds = Array.from(appointmentInspectorIds).filter(
      (inspectorId) => !parameterInspectorIds.has(inspectorId),
    );
    if (missingParameterInspectorIds.length > 0) {
      const missingInspectorNames = missingParameterInspectorIds
        .map((id) => {
          const inspector = inspectorById.get(id) || {};
          const firstName = (inspector?.account?.firstName || '').toString();
          const lastName = (inspector?.account?.lastName || '').toString();
          const fullName = `${firstName} ${lastName}`.trim();
          return fullName || `#${id}`;
        })
        .join(', ');
      openNotification(
        'warning',
        'Uyarı',
        `PKM Uzmanı Planlaması sekmesinde seçilen uzmanlar Parametreler sekmesinde de seçilmelidir: ${missingInspectorNames}`,
      );
      return;
    }

    const missingOfferingAppointments = appointments.filter(
      (appointment) => appointment.offeringId == null,
    );
    if (missingOfferingAppointments.length > 0) {
      openNotification(
        'error',
        'Hata',
        'Hizmet grubu bilgisi eksik olan plan satırları bulunuyor. Parametre verilerini kontrol ediniz.',
      );
      return;
    }

    const appointmentBuckets = new Map<
      number,
      { startDate: string; endDate: string; appointmentDate: string }[]
    >();
    const firstAppointmentByInspector = new Map<
      number,
      { startDate: string; endDate: string; appointmentDate: string }
    >();
    appointmentDetails
      .slice()
      .sort((a, b) => a.startDate.localeCompare(b.startDate))
      .forEach((appt) => {
        const bucket = appointmentBuckets.get(appt.inspectorId) || [];
        bucket.push(appt);
        appointmentBuckets.set(appt.inspectorId, bucket);
        if (!firstAppointmentByInspector.has(appt.inspectorId)) {
          firstAppointmentByInspector.set(appt.inspectorId, appt);
        }
      });

    const missingInspectors = new Set<number>();
    const tasks: TaskPayloadItem[] = effectivePlans
      .map((pl: SelectedPlanItem): TaskPayloadItem | null => {
        const inspectorId = Number(pl?.inspectorId);
        const attributeId = Number(pl?.attributeId);
        const qty = Number(pl?.qty);

        const bucket = appointmentBuckets.get(inspectorId);
        let assigned = pl.appointmentId != null
          ? appointmentDetails.find((appointment) => appointment.appointmentId === pl.appointmentId)
          : bucket && bucket.length ? bucket.shift() : undefined;
        if (!assigned) {
          assigned = firstAppointmentByInspector.get(inspectorId);
        }

        if (!assigned || !assigned.startDate) {
          missingInspectors.add(inspectorId);
          return null;
        }

        const appointmentDate = assigned.appointmentDate
          ? assigned.appointmentDate
          : dayjs(assigned.startDate).format('YYYY-MM-DD');

        return {
          inspectorId,
          attributeId,
          qty,
          date: pl.taskDate ?? assigned.startDate,
          appointmentDate,
          status: pl.taskStatus ?? planStatus,
          taskId: pl.taskId,
          appointmentId: pl.appointmentId,
        };
      })
      .filter((task: TaskPayloadItem | null): task is TaskPayloadItem => Boolean(task));

    if (effectivePlans.length > 0 && tasks.length === 0) {
      openNotification('error', 'Hata', 'Planlanan parametreler için geçerli bir kayıt bulunamadı.');
      return;
    }

    if (tasks.length === 0) {
      openNotification('error', 'Hata', 'En az bir parametre için plan oluşturunuz.');
      return;
    }

    if (missingInspectors.size > 0) {
      const missingNames = Array.from(missingInspectors)
        .map((id) => {
          const ins = inspectorById.get(id) || {};
          const first = ins?.account?.firstName || '';
          const last = ins?.account?.lastName || '';
          const name = `${first} ${last}`.trim();
          return name || `#${id}`;
        })
        .join(', ');
      openNotification('error', 'Hata', `Haftalık planda tarih seçilmeyen uzmanlar: ${missingNames}`);
      return;
    }

    const availabilityCache = new Map<string, boolean>();
    for (const appointment of appointments) {
      const cacheKey = `${appointment.inspectorId}|${appointment.startDate}|${appointment.endDate}|${appointment.ignoreAppointmentId ?? ''}`;
      if (!availabilityCache.has(cacheKey)) {
        try {
          const response = await checkInspectorAvailability(
            appointment.inspectorId,
            appointment.startDate,
            appointment.endDate,
            appointment.ignoreAppointmentId,
          );
          availabilityCache.set(cacheKey, response.available);
          if (!response.available) {
            const inspector = inspectorById.get(appointment.inspectorId) || {};
            const firstName = inspector?.account?.firstName || '';
            const lastName = inspector?.account?.lastName || '';
            const inspectorName = `${firstName} ${lastName}`.trim() || `#${appointment.inspectorId}`;
            const startMoment = dayjs(appointment.startDate);
            const endMoment = dayjs(appointment.endDate);
            const formattedRange =
              startMoment.isValid() && endMoment.isValid()
                ? `${startMoment.format('DD/MM/YYYY HH:mm')} - ${endMoment.format('DD/MM/YYYY HH:mm')}`
                : 'seçilen zaman aralığı';
            const availabilityLabel = availabilityMode === 'HOURLY' ? 'saatlik' : 'günlük';
            openNotification(
              'error',
              'Hata',
              `${inspectorName} ${availabilityLabel} uygunluk kuralına göre ${formattedRange} aralığında müsait değil.`,
            );
            return;
          }
        } catch (error) {
          console.error('Inspector availability check failed:', error);
          openNotification('error', 'Hata', 'Uzman uygunluk kontrolü yapılamadı. Lütfen tekrar deneyiniz.');
          return;
        }
      } else if (!availabilityCache.get(cacheKey)) {
        const inspector = inspectorById.get(appointment.inspectorId) || {};
        const firstName = inspector?.account?.firstName || '';
        const lastName = inspector?.account?.lastName || '';
        const inspectorName = `${firstName} ${lastName}`.trim() || `#${appointment.inspectorId}`;
        openNotification('error', 'Hata', `${inspectorName} seçilen zaman aralığında müsait değil.`);
        return;
      }
    }

    const payload = {
      proposalId,
      appointments: appointments.map(({ inspectorId, ignoreAppointmentId, ...rest }) => ({
        accountId: inspectorId,
        ...rest,
      })),
      tasks: tasks.map((task: TaskPayloadItem) => {
        const { inspectorId, ...rest } = task;
        return {
          accountId: inspectorId,
          ...rest,
        };
      }),
    };

    try {
      await savePlanningDetail(payload);
      if (planStatus === 'PLANNED') {
        if (proposalStatusId === 6) {
          try {
            await ProposalService.update(proposalId, { sendPlanNotifications: true });
          } catch (notifyError) {
            console.warn('Plan bildirimleri gönderilemedi:', notifyError);
          }
        } else if ((proposalStatusId === 5 || proposalStatusId === 7) && proposalStatusId != null) {
          try {
            await ProposalService.updateStatus(proposalId, proposalStatusId, { sendPlanNotifications: false });
          } catch (statusError) {
            console.warn('Plan sonrası statü geri yüklenemedi:', statusError);
          }
        }
      }
      openNotification(
        'success',
        'Başarılı',
        planStatus === 'RESERVE' ? 'Plan rezerv edildi.' : 'Plan kaydedildi.',
      );
      if (planStatus === 'PLANNED') {
        const statusTabMap: Record<number, string> = {
          5: '4',
          6: '2',
          7: '3',
        };
        const targetTab = proposalStatusId != null && statusTabMap[proposalStatusId]
          ? statusTabMap[proposalStatusId]
          : '3';
        navigate(`/project-followup?tab=${targetTab}`);
      }
    } catch (error) {
      console.error('Plan kaydedilirken hata oluştu:', error);
      openNotification('error', 'Hata', 'Plan kaydedilemedi.');
    }
  };

  const selectedParams = useMemo(() => {
    const rows = (proposal?.attributes || []).map((pa: any) => {
      const meta = attributesById.get(pa.attributeId) || {};
      const name = meta.name || `Parametre #${pa.attributeId}`;
      const unitPrice = pa.price ?? meta.price ?? 0;
      const quantity = pa.quantity ?? 0;
      const period = pa.period ?? 1;
      const rawOrder = meta?.order ?? meta?.orderNo ?? pa?.order ?? pa?.orderNo;
      const numericOrder = Number(rawOrder);
      const sortOrder = Number.isFinite(numericOrder) ? numericOrder : Number.MAX_SAFE_INTEGER;
      return {
        key: `${pa.attributeId}`,
        name,
        description: meta.description || '',
        quantity,
        period,
        unitPrice,
        total: unitPrice * quantity,
        sortOrder,
      };
    });
    // Adedi 0 olan parametreleri listeleme
    return rows
      .filter((r: any) => (Number(r?.quantity) || 0) > 0)
      .sort((a: any, b: any) => {
        if (a.sortOrder === b.sortOrder) {
          return (a.name || '').localeCompare(b.name || '');
        }
        return a.sortOrder - b.sortOrder;
      });
  }, [proposal, attributesById]);

  const parameterOptions = useMemo(
    () =>
      selectedParams.map((param: any) => {
        const attributeId = Number(param?.key);
        const meta = attributesById.get(attributeId) || {};
        const offeringId = coerceToNumber(meta?.offering?.id);
        const offeringName =
          typeof meta?.offering?.name === 'string' && meta.offering.name.trim().length > 0
            ? meta.offering.name.trim()
            : 'Diğer';
        return {
          attributeId,
          name: (param?.name || '').toString(),
          quantity: Number(param?.quantity) || 0,
          offeringKey: buildOfferingKey(offeringId, offeringName),
          offeringLabel: offeringName,
        };
      }).filter((param: any) => !Number.isNaN(param.attributeId) && param.attributeId > 0),
    [selectedParams, attributesById],
  );

  const parameterQuantityById = useMemo(() => {
    const map = new Map<number, number>();
    selectedParams.forEach((param: any) => {
      const attributeId = Number(param?.key);
      if (!Number.isNaN(attributeId) && attributeId > 0) {
        map.set(attributeId, Number(param?.quantity) || 0);
      }
    });
    return map;
  }, [selectedParams]);

  const plannedQuantityByAttributeId = useMemo(() => {
    const map = new Map<number, number>();
    (pagePlans || []).forEach((plan: any) => {
      const attributeId = Number(plan?.paramKey ?? plan?.attributeId);
      if (Number.isNaN(attributeId) || attributeId <= 0) {
        return;
      }
      const count = Number(plan?.count) || 0;
      if (count <= 0) {
        return;
      }
      map.set(attributeId, (map.get(attributeId) || 0) + count);
    });
    return map;
  }, [pagePlans]);

  const draftParametersBySlot = useMemo(() => {
    const slotMap: Record<string, Array<{ attributeId: number; name: string; quantity: number }>> = {};
    const plans = Array.isArray(pagePlans) ? pagePlans : [];

    Object.entries(offeringDates || {}).forEach(([offeringKey, ranges]) => {
      const assignedInspectors = offeringInspectors?.[offeringKey] || [];
      (ranges || []).forEach((range, index) => {
        const start = range?.[0];
        if (!start || !dayjs.isDayjs(start) || !start.isValid()) {
          return;
        }
        const inspectorId = Number(assignedInspectors[index]);
        if (Number.isNaN(inspectorId) || inspectorId <= 0) {
          return;
        }
        const dateLabel = start.format('DD/MM/YYYY HH:mm');
        const quantityByAttribute = new Map<number, number>();

        plans.forEach((plan: any) => {
          const attributeId = Number(plan?.paramKey ?? plan?.attributeId);
          if (Number.isNaN(attributeId) || attributeId <= 0) {
            return;
          }
          const meta = attributesById.get(attributeId) || {};
          const planOfferingKey = plan.offeringKey ?? buildOfferingKey(meta?.offering?.id, meta?.offering?.name);
          if (planOfferingKey !== offeringKey) {
            return;
          }
          const planInspectorId = Number(plan?.inspectorId);
          if (planInspectorId !== inspectorId) {
            return;
          }
          if (typeof plan?.date !== 'string' || plan.date !== dateLabel) {
            return;
          }
          const count = Number(plan?.count) || 0;
          if (count <= 0) {
            return;
          }
          quantityByAttribute.set(attributeId, (quantityByAttribute.get(attributeId) || 0) + count);
        });

        if (quantityByAttribute.size === 0) {
          return;
        }

        const slotKey = `${offeringKey}::${index}`;
        slotMap[slotKey] = Array.from(quantityByAttribute.entries())
          .map(([attributeId, quantity]) => {
            const meta = attributesById.get(attributeId) || {};
            return {
              attributeId,
              name: (meta?.name || `Parametre #${attributeId}`).toString(),
              quantity,
            };
          })
          .sort((a, b) => a.name.localeCompare(b.name, 'tr'));
      });
    });

    return slotMap;
  }, [pagePlans, offeringDates, offeringInspectors, attributesById]);

  // fieldControlTime dakikalarını offeringId + isim kombinasyonuna göre gruplayıp toplayalım
  const {
    fieldControlSummary,
    offeringLabelsByKey,
    offeringIdByKey,
    offeringKeyById,
  }: OfferingKeyMaps = useMemo(() => {
    const summary: Record<string, number> = {};
    const labels: Record<string, string> = {};
    const idByKey: Record<string, number | null> = {};
    const keyById: Record<string, string> = {};

    (proposal?.attributes || []).forEach((pa: any) => {
      const meta = attributesById.get(pa.attributeId) || {};
      const minutes = (meta?.fieldControlTime || 0) * (pa?.quantity || 0) * (pa?.period || 1);
      const rawOffering = meta?.offering || {};
      const offeringId = coerceToNumber(rawOffering?.id);
      const offeringName =
        typeof rawOffering?.name === 'string' && rawOffering.name.trim().length > 0
          ? rawOffering.name.trim()
          : 'Diğer';
      const key = buildOfferingKey(offeringId, offeringName);
      summary[key] = (summary[key] || 0) + minutes;
      if (!(key in labels)) {
        labels[key] = offeringName;
      }
      if (!(key in idByKey)) {
        idByKey[key] = offeringId ?? null;
      }
      if (offeringId != null && !(String(offeringId) in keyById)) {
        keyById[String(offeringId)] = key;
      }
    });

    return {
      fieldControlSummary: summary,
      offeringLabelsByKey: labels,
      offeringIdByKey: idByKey,
      offeringKeyById: keyById,
    };
  }, [proposal, attributesById]);

  const machineElectricHygieneSummary = useMemo(() => {
    const proposalAttributes = proposal?.attributes || [];
    let totalMakine = 0;
    let totalIsHijyeni = 0;
    let totalElektrik = 0;

    proposalAttributes.forEach((pa: any) => {
      const meta = attributesById.get(pa.attributeId) || {};
      const fieldTime = Number(meta?.fieldControlTime) || 0;
      const qty = Number(pa?.quantity) || 0;
      const period = Number(pa?.period) || 0;
      const partial = fieldTime * (period * qty);
      const offeringName = (meta?.offering?.name || '').toString();

      if (offeringName === 'Makine') {
        totalMakine += partial;
      } else if (offeringName === 'İş Hijyeni') {
        totalIsHijyeni += partial;
      } else if (offeringName === 'Elektrik') {
        totalElektrik += partial;
      }
    });

    const formatExactDays = (minutes: number) => {
      if (minutes <= 0) return '-';
      const exactDays = minutes / 480;
      return exactDays.toFixed(2);
    };
    const formatCeilDays = (minutes: number) => (minutes <= 0 ? '-' : `${Math.ceil(minutes / 480)}`);

    return {
      machine: {
        exact: formatExactDays(totalMakine),
        ceil: formatCeilDays(totalMakine),
      },
      electric: {
        exact: formatExactDays(totalElektrik),
        ceil: formatCeilDays(totalElektrik),
      },
      hygiene: {
        exact: formatExactDays(totalIsHijyeni),
        ceil: formatCeilDays(totalIsHijyeni),
      },
    };
  }, [proposal, attributesById]);

  // Parametre kartındaki P.K.M seçimlerine göre pivot tablo (Kullanıcı x Kategori)
  const groupPivot = useMemo(() => {
    // Map: key -> { name, sortOrder }
    const groupMetaMap = new Map<string, { name: string; sortOrder: number }>();
    const userMap = new Map<number, { inspectorName: string; counts: Record<string, number> }>();
    const inspectorNameById = new Map<number, string>();

    (inspectorList || []).forEach((inspector: any) => {
      const inspectorId = Number(inspector?.account?.id ?? inspector?.id ?? inspector?.accountId);
      if (Number.isNaN(inspectorId) || inspectorId <= 0) {
        return;
      }
      const firstName = (inspector?.account?.firstName ?? inspector?.firstName ?? '').toString().trim();
      const lastName = (inspector?.account?.lastName ?? inspector?.lastName ?? '').toString().trim();
      const fullName = `${firstName} ${lastName}`.trim();
      inspectorNameById.set(inspectorId, fullName.length > 0 ? fullName : `#${inspectorId}`);
    });

    // 1) Tüm kategorileri backend'den alınan tam listeden ekle
    (allGroups || []).forEach((g: any) => {
      if (g && g.id != null) {
        const key = `g_${g.id}`;
        const name = g.name || `Kategori #${g.id}`;
        const sortOrder = typeof g.sortOrder === 'number' ? g.sortOrder : 999999;
        groupMetaMap.set(key, { name, sortOrder });
      }
    });

    // 2) Parametre seçimlerinden kullanıcı başına sayıları topla
    (selectedParams || []).forEach((param: any) => {
      const attrId = Number(param?.key);
      if (Number.isNaN(attrId) || attrId <= 0) {
        return;
      }
      const meta = attributesById.get(attrId) || {};
      const gid = meta?.groupId ?? null;
      const gname: string = meta?.groupName ?? 'Kategori Yok';
      const gkey = gid != null ? `g_${gid}` : 'no-group';
      if (!groupMetaMap.has(gkey)) {
        // plan kaynaklı yeni kategori (veya 'no-group') — sortOrder bilinmiyorsa sona at
        groupMetaMap.set(gkey, { name: gname, sortOrder: gkey === 'no-group' ? 9999999 : 999999 });
      }

      const allocations = parameterInspectorSelections?.[attrId] || [];
      allocations.forEach((allocation) => {
        const uid = Number(allocation?.inspectorId);
        if (Number.isNaN(uid) || uid <= 0) {
          return;
        }
        const rec = userMap.get(uid) || {
          inspectorName: inspectorNameById.get(uid) || `#${uid}`,
          counts: {} as Record<string, number>,
        };
        userMap.set(uid, rec);
        const rawCount = Number(allocation?.count) || 0;
        const add = meta?.countable === false ? (rawCount > 0 ? 1 : 0) : rawCount;
        if (add <= 0) {
          return;
        }
        rec.counts[gkey] = (rec.counts[gkey] || 0) + add;
      });
    });

    const cols = Array.from(groupMetaMap.entries())
      .sort((a, b) => {
        const diff = (a[1].sortOrder ?? 999999) - (b[1].sortOrder ?? 999999);
        if (diff !== 0) return diff;
        return a[1].name.toString().localeCompare(b[1].name.toString(), 'tr');
      })
      .map(([key, meta]) => ({ key, name: meta.name }));

    const rows = Array.from(userMap.entries()).map(([uid, rec]) => {
      const row: any = { key: uid, inspectorName: rec.inspectorName };
      cols.forEach((c) => (row[c.key] = rec.counts[c.key] ?? 0));
      return row;
    });

    return { cols, rows };
  }, [selectedParams, parameterInspectorSelections, attributesById, allGroups, inspectorList]);

  // Inspector lookup map
  const inspectorById = useMemo(() => {
    const map = new Map<number, any>();
    inspectorList.forEach((ins: any) => {
      if (!ins) {
        return;
      }
      const accountId = Number(ins?.account?.id);
      if (!Number.isNaN(accountId)) {
        map.set(accountId, ins);
        return;
      }
      if (ins.id != null) {
        const legacyId = Number(ins.id);
        if (!Number.isNaN(legacyId)) {
          map.set(legacyId, ins);
        }
      }
    });
  return map;
}, [inspectorList]);

  const inspectorHasDrivingLicense = useCallback(
    (inspectorId?: number) => {
      if (!inspectorId) {
        return false;
      }
      const inspector = inspectorById.get(inspectorId);
      if (!inspector) {
        return false;
      }
      const primary = normalizeLicenseList(inspector?.drivingLicenseClasses);
      if (primary.length > 0) {
        return true;
      }
      const fallback = normalizeLicenseList(inspector?.drivingLicenseClass);
      return fallback.length > 0;
    },
    [inspectorById],
  );

  const resolveInspectorAccountId = useCallback((inspector: any): number | null => {
    const accountId = Number(inspector?.account?.id ?? inspector?.id ?? inspector?.accountId);
    if (Number.isNaN(accountId) || accountId <= 0) {
      return null;
    }
    return accountId;
  }, []);

  const resolveInspectorName = useCallback((inspector: any, fallbackId?: number): string => {
    const firstName = (inspector?.account?.firstName ?? inspector?.firstName ?? '').toString().trim();
    const lastName = (inspector?.account?.lastName ?? inspector?.lastName ?? '').toString().trim();
    const name = `${firstName} ${lastName}`.trim();
    if (name.length > 0) {
      return name;
    }
    if (fallbackId && fallbackId > 0) {
      return `#${fallbackId}`;
    }
    return 'Uzman';
  }, []);

  const selectedDraftInspectorIds = useMemo(() => {
    const ids = new Set<number>();
    Object.values(offeringInspectors || {}).forEach((list) => {
      (list || []).forEach((value) => {
        const inspectorId = Number(value);
        if (!Number.isNaN(inspectorId) && inspectorId > 0) {
          ids.add(inspectorId);
        }
      });
    });
    (pagePlans || []).forEach((plan: any) => {
      const inspectorId = Number(plan?.inspectorId);
      if (!Number.isNaN(inspectorId) && inspectorId > 0) {
        ids.add(inspectorId);
      }
    });
    return Array.from(ids);
  }, [offeringInspectors, pagePlans]);

  const selectedDraftInspectors = useMemo(() => {
    const found = new Map<number, any>();
    selectedDraftInspectorIds.forEach((inspectorId) => {
      const inspector =
        inspectorById.get(inspectorId) ||
        inspectorList.find((item: any) => resolveInspectorAccountId(item) === inspectorId);
      if (!inspector) {
        return;
      }
      found.set(inspectorId, inspector);
    });
    return Array.from(found.values());
  }, [selectedDraftInspectorIds, inspectorById, inspectorList, resolveInspectorAccountId]);

  const transportLabelByValue = useMemo(() => {
    const map = new Map<string, string>();
    (TRANSPORTATION_TYPE_OPTIONS || []).forEach((option: any) => {
      if (!option || option.value == null) {
        return;
      }
      map.set(String(option.value), String(option.label ?? option.value));
    });
    return map;
  }, []);

  const dailySelectedInspectors = useMemo(() => {
    const dayMap = new Map<
      string,
      Map<number, { slots: Array<{ key: string; startEpoch: number; timeLabel: string; transportLabel: string }>; slotKeySet: Set<string> }>
    >();

    Object.entries(offeringDates || {}).forEach(([offeringKey, ranges]) => {
      const assignedInspectors = offeringInspectors?.[offeringKey] || [];
      const transports = offeringTransports?.[offeringKey] || [];

      (ranges || []).forEach((range, index) => {
        const start = range?.[0];
        if (!start || !dayjs.isDayjs(start) || !start.isValid()) {
          return;
        }
        const end = range?.[1];
        const inspectorId = Number(assignedInspectors[index]);
        if (Number.isNaN(inspectorId) || inspectorId <= 0) {
          return;
        }
        const endMoment = end && dayjs.isDayjs(end) && end.isValid() ? end : null;
        const startEpoch = start.valueOf();
        const timeLabel = `${start.format('HH:mm')} - ${endMoment ? endMoment.format('HH:mm') : '--:--'}`;
        const transportValue = normalizeTransportValue(transports[index]);
        const transportLabel = transportValue
          ? transportLabelByValue.get(transportValue) || transportValue
          : transportLabelByValue.get('PUBLIC_TRANSPORT') || 'Toplu Taşıma';
        const slotKey = `${startEpoch}-${endMoment ? endMoment.valueOf() : 'na'}-${transportLabel}`;
        const dayKey = start.format('YYYY-MM-DD');
        const inspectorsByDay = dayMap.get(dayKey) || new Map<number, { slots: Array<{ key: string; startEpoch: number; timeLabel: string; transportLabel: string }>; slotKeySet: Set<string> }>();
        const current = inspectorsByDay.get(inspectorId) || { slots: [], slotKeySet: new Set<string>() };
        if (!current.slotKeySet.has(slotKey)) {
          current.slots.push({
            key: slotKey,
            startEpoch,
            timeLabel,
            transportLabel,
          });
          current.slotKeySet.add(slotKey);
        }
        inspectorsByDay.set(inspectorId, current);
        dayMap.set(dayKey, inspectorsByDay);
      });
    });

    return Array.from(dayMap.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([dayKey, inspectorsByDay]) => {
        const inspectors = Array.from(inspectorsByDay.entries())
          .map(([inspectorId, info]) => {
            const inspector = inspectorById.get(inspectorId) || {};
            const firstName = (inspector?.account?.firstName ?? inspector?.firstName ?? '').toString().trim();
            const lastName = (inspector?.account?.lastName ?? inspector?.lastName ?? '').toString().trim();
            const name = `${firstName} ${lastName}`.trim() || `#${inspectorId}`;
            const identityNumber = (inspector?.identityNumber ?? inspector?.account?.identityNumber ?? '').toString().trim() || undefined;
            const slots = (info?.slots || [])
              .slice()
              .sort((left, right) => left.startEpoch - right.startEpoch);
            return {
              id: inspectorId,
              name,
              identityNumber,
              slots,
            };
          })
          .sort((left, right) => left.name.localeCompare(right.name, 'tr'));

        return {
          dayKey,
          dayLabel: dayjs(dayKey, 'YYYY-MM-DD').format('DD/MM/YYYY'),
          inspectors,
        };
      });
  }, [offeringDates, offeringInspectors, offeringTransports, inspectorById, transportLabelByValue]);

  const matchedDraftInspectorOptionsByAttributeId = useMemo(() => {
    const map = new Map<number, Array<{ value: number; label: string }>>();
    selectedParams.forEach((param: any) => {
      const attributeId = Number(param?.key);
      if (Number.isNaN(attributeId) || attributeId <= 0) {
        return;
      }
      const optionMap = new Map<number, string>();
      selectedDraftInspectors.forEach((inspector: any) => {
        const inspectorId = resolveInspectorAccountId(inspector);
        if (inspectorId == null) {
          return;
        }
        const inspectorAttributeIds = extractNumericIds(inspector?.attributes);
        if (!inspectorAttributeIds.includes(attributeId)) {
          return;
        }
        optionMap.set(inspectorId, resolveInspectorName(inspector, inspectorId));
      });
      const options = Array.from(optionMap.entries())
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label, 'tr'));
      map.set(attributeId, options);
    });
    return map;
  }, [selectedParams, selectedDraftInspectors, resolveInspectorAccountId, resolveInspectorName]);

  const preselectedInspectorsByAttributeId = useMemo(() => {
    const map = new Map<number, ParameterInspectorAllocation[]>();
    selectedParams.forEach((param: any) => {
      const attributeId = Number(param?.key);
      if (Number.isNaN(attributeId) || attributeId <= 0) {
        return;
      }
      const maxSelectable = Math.max(1, Number(param?.quantity) || 1);
      const inspectorTotals = new Map<number, number>();
      (pagePlans || []).forEach((plan: any) => {
        const planAttributeId = Number(plan?.paramKey ?? plan?.attributeId);
        if (planAttributeId !== attributeId) {
          return;
        }
        const inspectorId = Number(plan?.inspectorId);
        if (Number.isNaN(inspectorId) || inspectorId <= 0) {
          return;
        }
        const qty = Math.max(1, Number(plan?.count) || 0);
        inspectorTotals.set(inspectorId, (inspectorTotals.get(inspectorId) || 0) + qty);
      });
      if (inspectorTotals.size === 0) {
        return;
      }
      const allocations = Array.from(inspectorTotals.entries())
        .sort((a, b) => {
          if (a[1] !== b[1]) {
            return b[1] - a[1];
          }
          return a[0] - b[0];
        })
        .slice(0, maxSelectable)
        .map(([inspectorId, count]) => ({
          inspectorId,
          count: Math.max(1, Math.floor(Number(count) || 0)),
        }));
      if (allocations.length > 0) {
        map.set(attributeId, allocations);
      }
    });
    return map;
  }, [selectedParams, pagePlans]);

  useEffect(() => {
    setParameterInspectorSelections((prev) => {
      let changed = false;
      const next: Record<number, ParameterInspectorAllocation[]> = {};

      matchedDraftInspectorOptionsByAttributeId.forEach((options, attributeId) => {
        const quantity = Math.max(1, Number(parameterQuantityById.get(attributeId)) || 0);
        const maxSelectable = quantity > 1 && options.length > 1 ? quantity : 1;
        const optionValueSet = new Set(options.map((option) => option.value));
        const currentAllocations = normalizeParameterInspectorAllocations(
          prev?.[attributeId] || [],
          optionValueSet,
          maxSelectable,
          quantity,
        );

        if (currentAllocations.length > 0) {
          next[attributeId] = currentAllocations;
          if (!areParameterInspectorAllocationsEqual(prev?.[attributeId], currentAllocations)) {
            changed = true;
          }
          return;
        }

        if ((prev?.[attributeId] || []).length > 0) {
          changed = true;
        }

        const preselectedAllocations = normalizeParameterInspectorAllocations(
          preselectedInspectorsByAttributeId.get(attributeId) || [],
          optionValueSet,
          maxSelectable,
          quantity,
        );
        if (preselectedAllocations.length > 0) {
          next[attributeId] = preselectedAllocations;
          if (!areParameterInspectorAllocationsEqual(prev?.[attributeId], preselectedAllocations)) {
            changed = true;
          }
          return;
        }

        if (options.length === 1) {
          const fallbackAllocations = normalizeParameterInspectorAllocations(
            [{ inspectorId: options[0].value, count: quantity }],
            optionValueSet,
            maxSelectable,
            quantity,
          );
          next[attributeId] = fallbackAllocations;
          if (!areParameterInspectorAllocationsEqual(prev?.[attributeId], fallbackAllocations)) {
            changed = true;
          }
        }
      });

      Object.keys(prev).forEach((attributeKey) => {
        const attributeId = Number(attributeKey);
        if (!matchedDraftInspectorOptionsByAttributeId.has(attributeId)) {
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [matchedDraftInspectorOptionsByAttributeId, preselectedInspectorsByAttributeId, parameterQuantityById]);

  const handleParameterInspectorSelectChange = useCallback((
    attributeId: number,
    value?: number | number[],
    maxSelection = 1,
    quantity = 1,
  ) => {
    setParameterInspectorSelections((prev) => {
      const next = { ...prev };
      const currentAllocations = prev?.[attributeId] || [];
      const normalizedValues = (Array.isArray(value) ? value : [value])
        .map((entry) => Number(entry))
        .filter((entry) => !Number.isNaN(entry) && entry > 0);
      const uniqueValues = Array.from(new Set(normalizedValues)).slice(0, Math.max(1, maxSelection));

      if (uniqueValues.length === 0) {
        if (!(attributeId in next)) {
          return prev;
        }
        delete next[attributeId];
        return next;
      }

      const created =
        uniqueValues.length === 1
          ? [{
            inspectorId: uniqueValues[0],
            count: Math.max(1, Math.floor(quantity) || 1),
          }]
          : splitQuantityAcrossInspectors(uniqueValues, quantity);

      const normalizedAllocations = normalizeParameterInspectorAllocations(
        created,
        new Set(uniqueValues),
        maxSelection,
        quantity,
      );

      if (areParameterInspectorAllocationsEqual(currentAllocations, normalizedAllocations)) {
        return prev;
      }
      next[attributeId] = normalizedAllocations;
      return next;
    });
  }, []);

  const handleParameterInspectorCountChange = useCallback((
    attributeId: number,
    inspectorId: number,
    value: number | null,
    maxSelection = 1,
    quantity = 1,
  ) => {
    setParameterInspectorSelections((prev) => {
      const currentAllocations = prev?.[attributeId] || [];
      if (currentAllocations.length === 0) {
        return prev;
      }
      const allowedInspectorIds = new Set<number>(
        currentAllocations
          .map((allocation) => Number(allocation?.inspectorId))
          .filter((entry) => !Number.isNaN(entry) && entry > 0),
      );
      if (!allowedInspectorIds.has(inspectorId)) {
        return prev;
      }
      const numericValue = Number(value);
      const desiredCount = Number.isFinite(numericValue) && numericValue > 0 ? Math.floor(numericValue) : 1;
      const updated = currentAllocations.map((allocation) =>
        Number(allocation.inspectorId) === inspectorId
          ? { ...allocation, count: desiredCount }
          : allocation);
      const normalizedAllocations = normalizeParameterInspectorAllocations(
        updated,
        allowedInspectorIds,
        maxSelection,
        quantity,
      );
      if (areParameterInspectorAllocationsEqual(currentAllocations, normalizedAllocations)) {
        return prev;
      }
      return {
        ...prev,
        [attributeId]: normalizedAllocations,
      };
    });
  }, []);

  const handleCreatePlanFromInspectorCard = useCallback((payload: {
    inspectorId: number;
    dateKey: string;
    start: dayjs.Dayjs;
    end: dayjs.Dayjs;
    transportationType?: string;
    note?: string;
    parameters: Array<{ attributeId: number; quantity: number }>;
  }) => {
    if (isReadOnly) {
      return;
    }

    const inspectorId = Number(payload?.inspectorId);
    if (Number.isNaN(inspectorId) || inspectorId <= 0) {
      openNotification('error', 'Hata', 'Geçerli bir uzman seçiniz.');
      return;
    }

    const start = payload?.start;
    const end = payload?.end;
    if (!start || !end || !dayjs.isDayjs(start) || !dayjs.isDayjs(end) || !start.isValid() || !end.isValid()) {
      openNotification('error', 'Hata', 'Geçerli tarih aralığı seçiniz.');
      return;
    }
    if (!end.isAfter(start)) {
      openNotification('error', 'Hata', 'Bitiş tarihi başlangıç tarihinden sonra olmalıdır.');
      return;
    }

    const selectedRows = (payload?.parameters || [])
      .map((row) => ({
        attributeId: Number(row?.attributeId),
        quantity: Number(row?.quantity) || 0,
      }))
      .filter((row) => !Number.isNaN(row.attributeId) && row.attributeId > 0 && row.quantity > 0);

    const groupedByOffering = new Map<string, Array<{ attributeId: number; quantity: number }>>();
    selectedRows.forEach((row) => {
      const meta = attributesById.get(row.attributeId) || {};
      const offeringId = coerceToNumber(meta?.offering?.id);
      const offeringName =
        typeof meta?.offering?.name === 'string' && meta.offering.name.trim().length > 0
          ? meta.offering.name.trim()
          : 'Diğer';
      const offeringKey = buildOfferingKey(offeringId, offeringName);
      const current = groupedByOffering.get(offeringKey) || [];
      current.push(row);
      groupedByOffering.set(offeringKey, current);
    });

    if (groupedByOffering.size === 0) {
      const fallbackOfferingKey =
        Object.keys(offeringDates || {})[0] ||
        Object.keys(fieldControlSummary || {}).find((key) => (fieldControlSummary[key] || 0) > 0) ||
        Object.keys(offeringLabelsByKey || {})[0] ||
        buildOfferingKey(null, 'Diğer');
      groupedByOffering.set(fallbackOfferingKey, []);
    }

    const requestedDayKey = start.format('YYYY-MM-DD');
    const hasInspectorAlreadyAssignedOnSameDay = Object.keys(offeringDates || {}).some((offeringKey) => {
      const inspectorList = offeringInspectors?.[offeringKey] || [];
      const dateRanges = offeringDates?.[offeringKey] || [];
      const maxLength = Math.max(inspectorList.length, dateRanges.length);
      for (let idx = 0; idx < maxLength; idx += 1) {
        const existingInspectorId = Number(inspectorList[idx]);
        if (Number.isNaN(existingInspectorId) || existingInspectorId !== inspectorId) {
          continue;
        }
        const existingStart = dateRanges[idx]?.[0];
        if (!existingStart || !dayjs.isDayjs(existingStart) || !existingStart.isValid()) {
          continue;
        }
        if (existingStart.format('YYYY-MM-DD') === requestedDayKey) {
          return true;
        }
      }
      return false;
    });
    if (hasInspectorAlreadyAssignedOnSameDay) {
      openNotification(
        'warning',
        'Uyarı',
        'Aynı teklifte aynı güne aynı P.K.M Uzmanı yalnızca bir kez planlanabilir.',
      );
      return;
    }

    const hasInspectorAlreadyAssignedOnSameOfferingAndDay = (offeringKey: string) => {
      const inspectorList = offeringInspectors?.[offeringKey] || [];
      const dateRanges = offeringDates?.[offeringKey] || [];
      const maxLength = Math.max(inspectorList.length, dateRanges.length);
      for (let idx = 0; idx < maxLength; idx += 1) {
        const existingInspectorId = Number(inspectorList[idx]);
        if (Number.isNaN(existingInspectorId) || existingInspectorId !== inspectorId) {
          continue;
        }
        const existingStart = dateRanges[idx]?.[0];
        if (!existingStart || !dayjs.isDayjs(existingStart) || !existingStart.isValid()) {
          continue;
        }
        if (existingStart.format('YYYY-MM-DD') === requestedDayKey) {
          return true;
        }
      }
      return false;
    };

    const duplicateOfferingKeys = Array.from(groupedByOffering.keys()).filter((offeringKey) =>
      hasInspectorAlreadyAssignedOnSameOfferingAndDay(offeringKey),
    );
    duplicateOfferingKeys.forEach((offeringKey) => groupedByOffering.delete(offeringKey));

    if (groupedByOffering.size === 0) {
      return;
    }

    const candidateOfferingKeys = Array.from(groupedByOffering.keys());
    const assignedOfferingKeysOnDay = new Set<string>();
    Object.keys(offeringDates || {}).forEach((existingOfferingKey) => {
      const inspectorList = offeringInspectors?.[existingOfferingKey] || [];
      const dateRanges = offeringDates?.[existingOfferingKey] || [];
      const maxLength = Math.max(inspectorList.length, dateRanges.length);
      for (let idx = 0; idx < maxLength; idx += 1) {
        const existingInspectorId = Number(inspectorList[idx]);
        if (Number.isNaN(existingInspectorId) || existingInspectorId !== inspectorId) {
          continue;
        }
        const existingStart = dateRanges[idx]?.[0];
        if (!existingStart || !dayjs.isDayjs(existingStart) || !existingStart.isValid()) {
          continue;
        }
        if (existingStart.format('YYYY-MM-DD') === requestedDayKey) {
          assignedOfferingKeysOnDay.add(existingOfferingKey);
          break;
        }
      }
    });

    const prioritizedOfferingKeys = candidateOfferingKeys.filter(
      (offeringKey) => !assignedOfferingKeysOnDay.has(offeringKey),
    );
    const targetOfferingKey = prioritizedOfferingKeys[0] ?? candidateOfferingKeys[0];
    if (!targetOfferingKey) {
      return;
    }

    const offeringKeys = [targetOfferingKey];
    const targetRows = groupedByOffering.get(targetOfferingKey) || [];
    const allowedAttributeIdSet = new Set<number>();
    targetRows.forEach((row) => {
      const attributeId = Number(row?.attributeId);
      if (!Number.isNaN(attributeId) && attributeId > 0) {
        allowedAttributeIdSet.add(attributeId);
      }
    });
    const filteredSelectedRows = selectedRows.filter((row) => {
      const attributeId = Number(row?.attributeId);
      if (Number.isNaN(attributeId) || attributeId <= 0) {
        return false;
      }
      return allowedAttributeIdSet.size === 0 ? true : allowedAttributeIdSet.has(attributeId);
    });

    const normalizedTransport = normalizeTransportValue(payload?.transportationType);
    const normalizedNote = normalizeNoteValue(payload?.note);

    setOfferingDates((prev) => {
      const next = { ...prev } as Record<string, DayRange[]>;
      offeringKeys.forEach((offeringKey) => {
        const list = next[offeringKey] ? [...next[offeringKey]] : [];
        list.push([start, end]);
        next[offeringKey] = list;
      });
      return next;
    });

    setOfferingInspectors((prev) => {
      const next = { ...prev } as Record<string, (number | undefined)[]>;
      offeringKeys.forEach((offeringKey) => {
        const list = next[offeringKey] ? [...next[offeringKey]] : [];
        list.push(inspectorId);
        next[offeringKey] = list;
      });
      return next;
    });

    setOfferingTransports((prev) => {
      const next = { ...prev } as Record<string, (string | undefined)[]>;
      offeringKeys.forEach((offeringKey) => {
        const list = next[offeringKey] ? [...next[offeringKey]] : [];
        list.push(normalizedTransport);
        next[offeringKey] = list;
      });
      return next;
    });

    setOfferingTransportNotes((prev) => {
      const next = { ...prev } as Record<string, (string | undefined)[]>;
      offeringKeys.forEach((offeringKey) => {
        const list = next[offeringKey] ? [...next[offeringKey]] : [];
        list.push(normalizedNote);
        next[offeringKey] = list;
      });
      return next;
    });

    setOfferingAppointmentIds((prev) => {
      const next = { ...prev } as Record<string, (number | undefined)[]>;
      offeringKeys.forEach((offeringKey) => {
        const list = next[offeringKey] ? [...next[offeringKey]] : [];
        list.push(undefined);
        next[offeringKey] = list;
      });
      return next;
    });

    setPreservedInspectorIdsByOffering((prev) => {
      const next = { ...prev } as Record<string, number[]>;
      offeringKeys.forEach((offeringKey) => {
        const set = new Set((next[offeringKey] || []).map((value) => Number(value)));
        set.add(inspectorId);
        next[offeringKey] = Array.from(set).filter((value) => !Number.isNaN(value) && value > 0);
      });
      return next;
    });

    const inspector = inspectorById.get(inspectorId) || {};
    const firstName = (inspector?.account?.firstName || '').toString();
    const lastName = (inspector?.account?.lastName || '').toString();
    const inspectorName = `${firstName} ${lastName}`.trim() || `#${inspectorId}`;
    const dateLabel = start.format('DD/MM/YYYY HH:mm');
    const uniqueSeed = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    const newPlans = filteredSelectedRows.map((row, index) => {
      const meta = attributesById.get(row.attributeId) || {};
      return {
        id: `manual-${uniqueSeed}-${index}`,
        paramKey: String(row.attributeId),
        paramName: meta?.name || `Parametre #${row.attributeId}`,
        paramQuantity: parameterQuantityById.get(row.attributeId),
        inspectorId,
        inspectorName,
        date: dateLabel,
        count: row.quantity,
      };
    });

    setPagePlans((prev) => [...prev, ...newPlans]);
    openNotification('success', 'Başarılı', 'Plan satırı eklendi.');
  }, [
    isReadOnly,
    attributesById,
    offeringDates,
    offeringInspectors,
    offeringLabelsByKey,
    fieldControlSummary,
    inspectorById,
    parameterQuantityById,
  ]);

  const handleUpdatePagePlanFromInspectorCard = useCallback(async (payload: {
    offeringKey: string;
    index: number;
    inspectorId: number;
    start: dayjs.Dayjs;
    end: dayjs.Dayjs;
    transportationType?: string;
    note?: string;
    parameters?: Array<{ attributeId: number; quantity: number }>;
  }) => {
    if (isReadOnly || savingPlanInFlight.current || updatingAppointmentInFlight.current || deletingAppointmentInFlight.current || pendingDeletedAppointmentId != null) {
      return false;
    }

    const offeringKey = payload?.offeringKey;
    const slotIndex = Number(payload?.index);
    const inspectorId = Number(payload?.inspectorId);
    const start = payload?.start;
    const end = payload?.end;

    if (!offeringKey || Number.isNaN(slotIndex) || slotIndex < 0) {
      openNotification('error', 'Hata', 'Plan satırı bulunamadı.');
      return false;
    }
    if (Number.isNaN(inspectorId) || inspectorId <= 0) {
      openNotification('error', 'Hata', 'Geçerli bir uzman seçiniz.');
      return false;
    }
    if (!start || !end || !dayjs.isDayjs(start) || !dayjs.isDayjs(end) || !start.isValid() || !end.isValid()) {
      openNotification('error', 'Hata', 'Geçerli tarih aralığı seçiniz.');
      return false;
    }
    if (!end.isAfter(start)) {
      openNotification('error', 'Hata', 'Bitiş tarihi başlangıç tarihinden sonra olmalıdır.');
      return false;
    }

    const currentRange = offeringDates?.[offeringKey]?.[slotIndex];
    if (!currentRange || !dayjs.isDayjs(currentRange[0]) || !currentRange[0]?.isValid()) {
      openNotification('error', 'Hata', 'Düzenlenecek plan satırı bulunamadı.');
      return false;
    }

    const oldStart = currentRange[0]!;
    const oldDateLabel = oldStart.format('DD/MM/YYYY HH:mm');
    const oldInspectorIdRaw = offeringInspectors?.[offeringKey]?.[slotIndex];
    const oldInspectorId = Number(oldInspectorIdRaw);
    const appointmentId = offeringAppointmentIds[offeringKey]?.[slotIndex];
    const requestedDayKey = start.format('YYYY-MM-DD');
    const hasInspectorAssignedOnOtherSlotSameDay = Object.keys(offeringDates || {}).some((existingOfferingKey) => {
      const inspectorList = offeringInspectors?.[existingOfferingKey] || [];
      const dateRanges = offeringDates?.[existingOfferingKey] || [];
      const maxLength = Math.max(inspectorList.length, dateRanges.length);
      for (let idx = 0; idx < maxLength; idx += 1) {
        if ((existingOfferingKey === offeringKey && idx === slotIndex)
          || (appointmentId != null && offeringAppointmentIds[existingOfferingKey]?.[idx] === appointmentId)) {
          continue;
        }
        const existingInspectorId = Number(inspectorList[idx]);
        if (Number.isNaN(existingInspectorId) || existingInspectorId !== inspectorId) {
          continue;
        }
        const existingStart = dateRanges[idx]?.[0];
        if (!existingStart || !dayjs.isDayjs(existingStart) || !existingStart.isValid()) {
          continue;
        }
        if (existingStart.format('YYYY-MM-DD') === requestedDayKey) {
          return true;
        }
      }
      return false;
    });
    if (hasInspectorAssignedOnOtherSlotSameDay) {
      openNotification(
        'warning',
        'Uyarı',
        'Aynı teklifte aynı güne aynı P.K.M Uzmanı yalnızca bir kez planlanabilir.',
      );
      return false;
    }
    if (!start.isSame(end, 'day')) {
      openNotification('error', 'Hata', 'Başlangıç ve bitiş aynı gün içinde olmalıdır.');
      return false;
    }
    if (publicHolidayDateSet.has(requestedDayKey)) {
      openNotification('error', 'Hata', 'Resmî tatil günlerinde plan oluşturulamaz.');
      return false;
    }
    updatingAppointmentInFlight.current = true;
    setUpdatingAppointment(true);
    try {
      const availability = await checkInspectorAvailability(inspectorId,
        start.format('YYYY-MM-DD[T]HH:mm:ss'), end.format('YYYY-MM-DD[T]HH:mm:ss'), appointmentId);
      if (!availability.available) {
        openNotification('error', 'Hata', 'Uzman seçilen tarih ve saat aralığında müsait değil.');
        return false;
      }
      const reservations = await listPlanningReservationsByRange(requestedDayKey, requestedDayKey);
      if ((reservations || []).some((reservation: any) =>
        Number(reservation.accountId ?? reservation.account?.id) === inspectorId)) {
        openNotification('error', 'Hata', 'Seçilen tarih uzman rezervasyonu ile çakışıyor.');
        return false;
      }
    } catch (error) {
      openNotification('error', 'Hata', error instanceof Error ? error.message : 'Uzman uygunluk kontrolü yapılamadı.');
      return false;
    } finally {
      updatingAppointmentInFlight.current = false;
      setUpdatingAppointment(false);
    }
    const normalizedTransport = normalizeTransportValue(payload?.transportationType);
    const normalizedNote = normalizeNoteValue(payload?.note);
    const selectedRows = (payload?.parameters || [])
      .map((row) => ({
        attributeId: Number(row?.attributeId),
        quantity: Number(row?.quantity) || 0,
      }))
      .filter((row) => !Number.isNaN(row.attributeId) && row.attributeId > 0 && row.quantity > 0);
    const hasParameterOverride = selectedRows.length > 0;

    const updateSlots = <T,>(previous: Record<string, T[]>, value: T): Record<string, T[]> =>
      Object.fromEntries(Object.entries(previous).map(([key, values]) => [key, values.map((existing, index) =>
        (key === offeringKey && index === slotIndex)
          || (appointmentId != null && offeringAppointmentIds[key]?.[index] === appointmentId) ? value : existing)]));
    setOfferingDates((previous) => updateSlots<DayRange>(previous, [start, end]));

    setOfferingInspectors((prev) => {
      const next = { ...prev } as Record<string, (number | undefined)[]>;
      const list = next[offeringKey] ? [...next[offeringKey]] : [];
      if (slotIndex >= list.length) {
        return prev;
      }
      list[slotIndex] = inspectorId;
      next[offeringKey] = list;
      return next;
    });

    setOfferingTransports((previous) => updateSlots(previous, normalizedTransport));
    setOfferingTransportNotes((previous) => updateSlots(previous, normalizedNote));

    setPreservedInspectorIdsByOffering((prev) => {
      const next = { ...prev } as Record<string, number[]>;
      const values = new Set((next[offeringKey] || []).map((value) => Number(value)).filter((value) => !Number.isNaN(value) && value > 0));
      values.add(inspectorId);
      next[offeringKey] = Array.from(values);
      return next;
    });

    const inspector = inspectorById.get(inspectorId) || {};
    const firstName = (inspector?.account?.firstName || '').toString();
    const lastName = (inspector?.account?.lastName || '').toString();
    const inspectorName = `${firstName} ${lastName}`.trim() || `#${inspectorId}`;
    const newDateLabel = start.format('DD/MM/YYYY HH:mm');

    setPagePlans((prev) => {
      const isMatchedPlan = (plan: any) => {
        if (appointmentId != null && plan.appointmentId != null) {
          return Number(plan.appointmentId) === appointmentId;
        }
        const attributeId = Number(plan?.paramKey ?? plan?.attributeId);
        const meta = attributesById.get(attributeId) || {};
        const planOfferingKey = plan.offeringKey ?? buildOfferingKey(meta?.offering?.id, meta?.offering?.name);
        if (planOfferingKey !== offeringKey) {
          return false;
        }
        const planInspectorId = Number(plan?.inspectorId);
        if (!Number.isNaN(oldInspectorId) && oldInspectorId > 0 && planInspectorId !== oldInspectorId) {
          return false;
        }
        if (typeof plan?.date === 'string' && plan.date !== oldDateLabel) {
          return false;
        }
        return true;
      };

      if (!hasParameterOverride) {
        return prev.map((plan: any) => (
          isMatchedPlan(plan)
            ? {
              ...plan,
              inspectorId,
              inspectorName,
              date: newDateLabel,
            }
            : plan
        ));
      }

      const matchedPlans = prev.filter(isMatchedPlan);
      const basePlans = prev.filter((plan: any) => !isMatchedPlan(plan));
      const uniqueSeed = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const replacementPlans = selectedRows.flatMap((row, index) => {
        let remaining = row.quantity;
        const retained: any[] = [];
        matchedPlans.filter((plan) => Number(plan.paramKey) === row.attributeId)
          .forEach((plan) => {
            const count = Math.min(remaining, Number(plan.count) || 0);
            if (count > 0) retained.push({ ...plan, count, inspectorId, inspectorName, date: newDateLabel,
              taskDate: plan.taskDate });
            remaining -= count;
          });
        if (remaining > 0) {
          const meta = attributesById.get(row.attributeId) || {};
          retained.push({
            id: `edit-${uniqueSeed}-${index}`,
            paramKey: String(row.attributeId),
            paramName: meta?.name || `Parametre #${row.attributeId}`,
            paramQuantity: parameterQuantityById.get(row.attributeId),
            inspectorId, inspectorName, offeringKey, date: newDateLabel, count: remaining,
          });
        }
        return retained;
      });
      return [...basePlans, ...replacementPlans];
    });

    openNotification('success', 'Başarılı', 'Plan satırı güncellendi. Değişiklikleri kaydetmek için Planı Tamamla düğmesini kullanınız.');
    return true;
  }, [
    isReadOnly,
    offeringDates,
    offeringAppointmentIds,
    pendingDeletedAppointmentId,
    publicHolidayDateSet,
    offeringInspectors,
    attributesById,
    inspectorById,
    parameterQuantityById,
  ]);

  const refreshDeletedAppointmentLinks = async () => {
    const refreshed = normalizePlanningBundle(await getInspectionPlanByProposal(Number(id)));
    const tasksById = new Map<number, any>(refreshed.tasks.map((task: any) => [Number(task.id), task]));
    const appointmentsById = new Map<number, any>(refreshed.appointments.map((appointment: any) => [Number(appointment.id), appointment]));
    setPagePlans((previous) => previous.map((plan) => {
      const task = tasksById.get(plan.taskId);
      if (!task || task.appointmentId === plan.appointmentId) return plan;
      const appointment = appointmentsById.get(Number(task.appointmentId));
      if (!appointment) return plan;
      // Use the target's local date if it has an unsaved edit.
      let date = dayjs(appointment.startDate).format('DD/MM/YYYY HH:mm');
      let offeringKey = offeringKeyById[String(appointment.offeringId)] ?? plan.offeringKey;
      Object.entries(offeringAppointmentIds).forEach(([key, ids]) => {
        const index = ids.indexOf(Number(task.appointmentId));
        const localStart = offeringDates[key]?.[index]?.[0];
        if (index >= 0 && localStart?.isValid()) {
          date = localStart.format('DD/MM/YYYY HH:mm');
          offeringKey = key;
        }
      });
      return { ...plan, appointmentId: Number(task.appointmentId), offeringKey, date };
    }));
    setExistingPlan(refreshed);
    setPendingDeletedAppointmentId(null);
  };

  const retryDeletedAppointmentRefresh = async () => {
    if (deletingAppointmentInFlight.current) return;
    deletingAppointmentInFlight.current = true;
    setDeletingAppointment(true);
    try {
      await refreshDeletedAppointmentLinks();
    } catch (error) {
      openNotification('error', 'Hata', 'Güncel plan yüklenemedi. Tekrar deneyiniz.');
    } finally {
      deletingAppointmentInFlight.current = false;
      setDeletingAppointment(false);
    }
  };

  const handleDeletePagePlanFromInspectorCard = async (payload: { offeringKey: string; index: number }) => {
    if (isReadOnly || savingPlanInFlight.current || updatingAppointmentInFlight.current || deletingAppointmentInFlight.current || pendingDeletedAppointmentId != null) return;
    const { offeringKey, index } = payload;
    const range = offeringDates[offeringKey]?.[index];
    if (!range) {
      openNotification('error', 'Hata', 'Plan satırı bulunamadı.');
      return;
    }
    const appointmentId = offeringAppointmentIds[offeringKey]?.[index];
    deletingAppointmentInFlight.current = true;
    setDeletingAppointment(true);
    let deleted = false;
    try {
      if (appointmentId != null) {
        await deleteInspectionAppointment(appointmentId);
        deleted = true;
        setPendingDeletedAppointmentId(appointmentId);
      }
      const keptIndexes = new Map<string, number[]>();
      Object.entries(offeringDates).forEach(([key, ranges]) => {
        keptIndexes.set(key, ranges.map((_, slotIndex) => slotIndex).filter((slotIndex) =>
          appointmentId != null
            ? offeringAppointmentIds[key]?.[slotIndex] !== appointmentId
            : key !== offeringKey || slotIndex !== index));
      });
      const removeSlots = <T,>(previous: Record<string, T[]>): Record<string, T[]> =>
        Object.fromEntries(Object.entries(previous).map(([key, values]) => [key,
          (keptIndexes.get(key) ?? []).map((slotIndex) => values[slotIndex]),
        ]).filter(([, values]) => (values as T[]).length > 0));
      setOfferingDates(removeSlots);
      setOfferingInspectors(removeSlots);
      setOfferingTransports(removeSlots);
      setOfferingTransportNotes(removeSlots);
      setOfferingAppointmentIds(removeSlots);
      if (appointmentId != null) {
        await refreshDeletedAppointmentLinks();
      } else {
        const inspectorId = offeringInspectors[offeringKey]?.[index];
        const dateLabel = range[0]?.format('DD/MM/YYYY HH:mm');
        setPagePlans((previous) => previous.filter((plan) => plan.taskId != null
          || plan.inspectorId !== inspectorId || plan.date !== dateLabel
          || (plan.offeringKey ?? buildOfferingKey(attributesById.get(Number(plan.paramKey))?.offering?.id,
            attributesById.get(Number(plan.paramKey))?.offering?.name)) !== offeringKey));
      }
      openNotification('success', 'Başarılı', 'Seçilen randevu silindi.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Randevu silinemedi.';
      openNotification('error', 'Hata', deleted
        ? 'Randevu silindi, güncel plan yüklenemedi. Kaydetmeden önce Planı Yenile düğmesini kullanınız.' : message);
    } finally {
      deletingAppointmentInFlight.current = false;
      setDeletingAppointment(false);
    }
  };

  const proposalAttributeRequirements = useMemo(() => {
    const map = new Map<number, AttributeRequirementInfo>();
    (proposal?.attributes || []).forEach((pa: any) => {
      const attrId = Number(pa?.attributeId);
      if (Number.isNaN(attrId)) {
        return;
      }
      const meta = attributesById.get(attrId) || {};
      const requiredProfessionIds = extractNumericIds(
        pa?.requiredProfessionIds ?? meta?.requiredProfessionIds,
      );
      const requiredMeasuringInstrumentIds = extractNumericIds(
        pa?.requiredMeasuringInstrumentIds ?? meta?.requiredMeasuringInstrumentIds,
      );
      map.set(attrId, {
        requiredProfessionIds,
        requiredMeasuringInstrumentIds,
      });
    });
    return map;
  }, [proposal, attributesById]);

  const requirementsByOfferingKey = useMemo(() => {
    const map = new Map<string, AttributeRequirementInfo[]>();
    (proposal?.attributes || []).forEach((pa: any) => {
      const attrId = Number(pa?.attributeId);
      if (Number.isNaN(attrId)) {
        return;
      }
      const meta = attributesById.get(attrId) || {};
      const offeringId = coerceToNumber(meta?.offering?.id);
      const offeringName: string =
        typeof meta?.offering?.name === 'string' && meta.offering.name.trim().length > 0
          ? meta.offering.name.trim()
          : 'Diğer';
      const offeringKey = buildOfferingKey(offeringId, offeringName);
      const requirement =
        proposalAttributeRequirements.get(attrId) ?? {
          requiredProfessionIds: extractNumericIds(meta?.requiredProfessionIds),
          requiredMeasuringInstrumentIds: extractNumericIds(meta?.requiredMeasuringInstrumentIds),
        };
      const existing = map.get(offeringKey);
      if (existing) {
        existing.push(requirement);
      } else {
        map.set(offeringKey, [requirement]);
      }
    });
    return map;
  }, [proposal, attributesById, proposalAttributeRequirements]);

  const eligibleInspectorsByAttributeId = useMemo(() => {
    const map = new Map<number, any[]>();
    proposalAttributeRequirements.forEach((requirement, attrId) => {
      map.set(attrId, filterInspectorsByRequirementSets(inspectorList, [requirement]));
    });
    return map;
  }, [proposalAttributeRequirements, inspectorList]);

  const eligibleInspectorsByOffering = useMemo(() => {
    const map = new Map<string, any[]>();
    requirementsByOfferingKey.forEach((reqList, offeringKey) => {
      map.set(offeringKey, filterInspectorsByRequirementSets(inspectorList, reqList));
    });
    return map;
  }, [requirementsByOfferingKey, inspectorList]);

  const attributeIdsByOffering = useMemo(() => {
    const map = new Map<string, number[]>();
    (proposal?.attributes || []).forEach((pa: any) => {
      const attrId = Number(pa?.attributeId);
      if (Number.isNaN(attrId)) {
        return;
      }
      const meta = attributesById.get(attrId) || {};
      const offeringId = coerceToNumber(meta?.offering?.id);
      const offeringName: string =
        typeof meta?.offering?.name === 'string' && meta.offering.name.trim().length > 0
          ? meta.offering.name.trim()
          : 'Diğer';
      const offeringKey = buildOfferingKey(offeringId, offeringName);
      const current = map.get(offeringKey);
      if (current) {
        current.push(attrId);
      } else {
        map.set(offeringKey, [attrId]);
      }
    });
    map.forEach((list, key) => {
      const unique = Array.from(
        new Set(
          list
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id) && id > 0),
        ),
      );
      map.set(key, unique);
    });
    return map;
  }, [proposal, attributesById]);

  const refreshAvailabilityForRange = useCallback(
    async (offering: string, index: number, start: dayjs.Dayjs, end: dayjs.Dayjs) => {
      if (
        !start ||
        !end ||
        !dayjs.isDayjs(start) ||
        !start.isValid() ||
        !dayjs.isDayjs(end) ||
        !end.isValid()
      ) {
        setAvailableInspectorsForRange(offering, index, undefined);
        setAvailabilityLoadingFlag(offering, index, false);
        return;
      }

      const normalizedEnd =
        end.isAfter(start)
          ? end
          : availabilityMode === 'DAILY'
            ? end.add(1, 'day')
            : end.add(1, 'minute');

      const startIso = start.toISOString();
      const endIso = normalizedEnd.toISOString();
      const attributeIds = attributeIdsByOffering.get(offering) ?? [];

      const eligibleList = eligibleInspectorsByOffering.get(offering);
      const hasEligibilitySubset = Array.isArray(eligibleList) && eligibleList.length > 0;
      const sourceList = hasEligibilitySubset ? eligibleList : inspectorList;
      const eligibleAccountIdSet = new Set<number>(
        (sourceList || [])
          .map((ins: any) => Number(ins?.account?.id ?? ins?.id))
          .filter((accountId) => !Number.isNaN(accountId) && accountId > 0),
      );
      const shouldFilterByEligibility = hasEligibilitySubset && eligibleAccountIdSet.size > 0;

      const includeAccountIdsSet = new Set<number>();
      Object.values(offeringInspectors || {}).forEach((list) => {
        (list || []).forEach((value) => {
          const numericId = Number(value);
          if (!Number.isNaN(numericId) && numericId > 0) {
            includeAccountIdsSet.add(numericId);
          }
        });
      });
      (pagePlans || []).forEach((plan) => {
        const numericId = Number(plan?.inspectorId);
        if (!Number.isNaN(numericId) && numericId > 0) {
          includeAccountIdsSet.add(numericId);
        }
      });
      const includeAccountIds =
        availabilityMode === 'DAILY' && includeAccountIdsSet.size > 0
          ? Array.from(includeAccountIdsSet)
          : undefined;

      setAvailabilityLoadingFlag(offering, index, true);

      try {
        const response = await fetchAvailableInspectorsForAttributes({
          attributeIds,
          start: startIso,
          end: endIso,
          includeAccountIds,
        });

        const availableIds = Array.from(
          new Set(
            (response?.availableAccountIds || [])
              .map((value) => Number(value))
              .filter((value) => !Number.isNaN(value) && value > 0),
          ),
        );

        let filteredIds = shouldFilterByEligibility
          ? availableIds.filter((accountId) => eligibleAccountIdSet.has(accountId))
          : availableIds;
        if (filteredIds.length > 0) {
          try {
            const reservationStart = start.startOf('day').format('YYYY-MM-DD');
            const reservationEnd = end.endOf('day').format('YYYY-MM-DD');
            const reservations = await listPlanningReservationsByRange(reservationStart, reservationEnd);
            const reservedAccountIds = new Set(
              (reservations || [])
                .map((reservation: any) => Number(reservation?.account?.id ?? reservation?.accountId))
                .filter((accountId) => !Number.isNaN(accountId) && accountId > 0),
            );
            if (reservedAccountIds.size > 0) {
              filteredIds = filteredIds.filter((accountId) => !reservedAccountIds.has(accountId));
            }
          } catch (reservationError) {
            console.error('Rezervasyon uygunluğu alınamadı:', reservationError);
          }
        }
        setAvailableInspectorsForRange(offering, index, filteredIds);
      } catch (error) {
        console.error('Inspector availability fetch failed:', error);
        openNotification('error', 'Hata', 'Uzman uygunlukları alınamadı. Lütfen tekrar deneyiniz.');
        setAvailableInspectorsForRange(offering, index, []);
      } finally {
        setAvailabilityLoadingFlag(offering, index, false);
      }
    },
    [
      attributeIdsByOffering,
      eligibleInspectorsByOffering,
      inspectorList,
      offeringInspectors,
      pagePlans,
      availabilityMode,
      setAvailableInspectorsForRange,
      setAvailabilityLoadingFlag,
    ],
  );

  useEffect(() => {
    if (isReadOnly) {
      return;
    }

    Object.entries(offeringDates || {}).forEach(([offering, ranges]) => {
      (ranges || []).forEach((range, index) => {
        const start = range?.[0];
        const end = range?.[1];
        if (dayjs.isDayjs(start) && start.isValid() && dayjs.isDayjs(end) && end.isValid()) {
          const allowed = availableInspectorIds?.[offering]?.[index];
          const loading = Boolean(availabilityLoading?.[offering]?.[index]);
          if (!Array.isArray(allowed) && !loading) {
            refreshAvailabilityForRange(offering, index, start, end);
          }
        } else {
          if (availableInspectorIds?.[offering]?.[index] !== undefined) {
            setAvailableInspectorsForRange(offering, index, undefined);
          }
          if (availabilityLoading?.[offering]?.[index]) {
            setAvailabilityLoadingFlag(offering, index, false);
          }
        }
      });
    });
  }, [
    offeringDates,
    availableInspectorIds,
    availabilityLoading,
    refreshAvailabilityForRange,
    setAvailableInspectorsForRange,
    setAvailabilityLoadingFlag,
    isReadOnly,
  ]);

  useEffect(() => {
    const activeOfferings = new Set(Object.keys(offeringDates || {}));

    setAvailableInspectorIds((prev) => {
      let changed = false;
      const next: Record<string, Record<number, number[]>> = {};

      Object.entries(prev).forEach(([offering, record]) => {
        if (!activeOfferings.has(offering)) {
          changed = true;
          return;
        }
        const ranges = offeringDates?.[offering] || [];
        const filtered: Record<number, number[]> = {};
        Object.entries(record).forEach(([idxStr, ids]) => {
          const idx = Number(idxStr);
          const range = ranges[idx];
          if (
            range &&
            dayjs.isDayjs(range[0]) &&
            range[0].isValid() &&
            dayjs.isDayjs(range[1]) &&
            range[1].isValid()
          ) {
            filtered[idx] = ids;
          } else {
            changed = true;
          }
        });
        if (Object.keys(filtered).length > 0) {
          next[offering] = filtered;
        } else if (Object.keys(record).length > 0) {
          changed = true;
        }
      });

      if (!changed) {
        return prev;
      }
      return next;
    });

    setAvailabilityLoading((prev) => {
      let changed = false;
      const next: Record<string, Record<number, boolean>> = {};

      Object.entries(prev).forEach(([offering, record]) => {
        if (!activeOfferings.has(offering)) {
          changed = true;
          return;
        }
        const ranges = offeringDates?.[offering] || [];
        const filtered: Record<number, boolean> = {};
        Object.entries(record).forEach(([idxStr, value]) => {
          const idx = Number(idxStr);
          const range = ranges[idx];
          if (
            range &&
            dayjs.isDayjs(range[0]) &&
            range[0].isValid() &&
            dayjs.isDayjs(range[1]) &&
            range[1].isValid()
          ) {
            filtered[idx] = value;
          } else {
            changed = true;
          }
        });
        if (Object.keys(filtered).length > 0) {
          next[offering] = filtered;
        } else if (Object.keys(record).length > 0) {
          changed = true;
        }
      });

      if (!changed) {
        return prev;
      }
      return next;
    });
  }, [offeringDates]);

  useEffect(() => {
    setOfferingAppointmentIds((prev) => {
      const offeringKeys = new Set([
        ...Object.keys(offeringDates || {}),
        ...Object.keys(offeringInspectors || {}),
      ]);
      let changed = false;
      const next: Record<string, (number | undefined)[]> = {};

      offeringKeys.forEach((offering) => {
        const targetLength = Math.max(
          (offeringDates?.[offering] || []).length,
          (offeringInspectors?.[offering] || []).length,
        );
        const prevList = prev[offering] || [];
        const adjusted = prevList.slice(0, targetLength);
        while (adjusted.length < targetLength) {
          adjusted.push(undefined);
        }
        if (
          prevList.length !== adjusted.length ||
          adjusted.some((val, idx) => val !== prevList[idx])
        ) {
          changed = true;
        }
        next[offering] = adjusted;
      });

      Object.keys(prev).forEach((offering) => {
        if (!offeringKeys.has(offering)) {
          changed = true;
        }
      });

      if (!changed) {
        return prev;
      }
      return next;
    });
  }, [offeringDates, offeringInspectors]);

  useEffect(() => {
    setOfferingInspectors((prev) => {
      let hasChanges = false;
      const next: Record<string, (number | undefined)[]> = {};

      Object.entries(prev).forEach(([offering, list]) => {
        const availability = availableInspectorIds[offering];
        const preservedSet = new Set(
          (preservedInspectorIdsByOffering[offering] || []).map((value) =>
            Number.isFinite(Number(value)) ? Number(value) : undefined,
          ).filter((value): value is number => value !== undefined),
        );
        if (!availability) {
          next[offering] = list;
          return;
        }

        const updatedList = list.map((value, idx) => {
          const allowedIds = availability[idx];
          if (!Array.isArray(allowedIds)) {
            return value;
          }

          if (allowedIds.length === 0) {
            if (value != null && preservedSet.has(Number(value))) {
              return value;
            }
            if (value == null) {
              return value;
            }
            hasChanges = true;
            return undefined;
          }

          if (value == null) {
            return value;
          }

          const numericId = Number(value);
          if (
            !Number.isNaN(numericId) &&
            (allowedIds.includes(numericId) || preservedSet.has(numericId))
          ) {
            return value;
          }

          hasChanges = true;
          return undefined;
        });

        next[offering] = updatedList;
        if (!hasChanges && updatedList.some((val, idx) => val !== list[idx])) {
          hasChanges = true;
        }
      });

      if (!hasChanges) {
        return prev;
      }

      return next;
    });
  }, [availableInspectorIds, preservedInspectorIdsByOffering]);

  const transportationGroups = useMemo<TransportationGroup[]>(() => {
    const groupMap = new Map<string, { date: dayjs.Dayjs; entries: Map<number, TransportationGroupEntry> }>();

    Object.entries(offeringDates || {}).forEach(([offering, ranges]) => {
      const inspectorList = offeringInspectors?.[offering] || [];
      const transportList = offeringTransports?.[offering] || [];
      const noteList = offeringTransportNotes?.[offering] || [];

      (ranges || []).forEach((range, index) => {
        const start = range?.[0];
        if (!start || !dayjs.isDayjs(start) || !start.isValid()) {
          return;
        }

        const inspectorId = Number(inspectorList[index]);
        if (Number.isNaN(inspectorId) || inspectorId <= 0) {
          return;
        }

        const normalizedDate = start.startOf('day');
        const dateKey = normalizedDate.format('DD/MM/YYYY');
        const inspector = inspectorById.get(inspectorId) || {};
        const firstName = inspector?.account?.firstName || '';
        const lastName = inspector?.account?.lastName || '';
        const inspectorName = `${firstName} ${lastName}`.trim() || `#${inspectorId}`;
        const normalizedTransport = normalizeTransportValue(transportList[index]);

        if (!groupMap.has(dateKey)) {
          groupMap.set(dateKey, {
            date: normalizedDate,
            entries: new Map<number, TransportationGroupEntry>(),
          });
        }

        const groupEntry = groupMap.get(dateKey);
        if (!groupEntry) {
          return;
        }

        const inspectorEntry = groupEntry.entries.get(inspectorId);
        const assignment: TransportationAssignment = {
          offering,
          index,
          transport: normalizedTransport,
          note: noteList[index],
        };

        if (!inspectorEntry) {
          groupEntry.entries.set(inspectorId, {
            key: `${dateKey}-${inspectorId}`,
            inspectorId,
            inspectorName,
            transport: normalizedTransport,
            note: noteList[index],
            assignments: [assignment],
          });
        } else {
          inspectorEntry.assignments.push(assignment);
        }
      });
    });

    const groups: TransportationGroup[] = Array.from(groupMap.entries()).map(([dateKey, { date, entries }]) => {
      const inspectorEntries = Array.from(entries.values()).map((entry) => {
        const definedValues = Array.from(
          new Set(
            entry.assignments
              .map((assignment) => normalizeTransportValue(assignment.transport))
              .filter((val): val is string => typeof val === 'string' && val.length > 0),
          ),
        );
        const transportValue = definedValues.length === 1 ? definedValues[0] : undefined;
        const definedNotes = Array.from(
          new Set(
            entry.assignments
              .map((assignment) => normalizeNoteValue(assignment.note))
              .filter((val): val is string => typeof val === 'string' && val.length > 0),
          ),
        );
        const noteValue = definedNotes.length === 1 ? definedNotes[0] : undefined;
        return {
          ...entry,
          transport: transportValue,
          note: noteValue,
        };
      });

      inspectorEntries.sort((a, b) => a.inspectorName.localeCompare(b.inspectorName, 'tr'));

      return {
        dateKey,
        date,
        entries: inspectorEntries,
      };
    });

    groups.sort((a, b) => a.date.valueOf() - b.date.valueOf());

    return groups;
  }, [offeringDates, offeringInspectors, offeringTransports, offeringTransportNotes, inspectorById]);

  useEffect(() => {
    const values: Record<string, string> = {};
    transportationGroups.forEach((group) => {
      group.entries.forEach((entry) => {
        values[`note-${entry.key}`] = entry.note ?? '';
      });
    });
    transportForm.setFieldsValue(values);
  }, [transportationGroups, transportForm]);

  useEffect(() => {
    if (!existingPlan || initialPlanApplied) {
      return;
    }

    const tasks: any[] = Array.isArray(existingPlan?.tasks) ? existingPlan.tasks : [];
    const appointments: any[] = Array.isArray(existingPlan?.appointments) ? existingPlan.appointments : [];

    const appointmentById = new Map<number, any>();
    const appointmentByInspectorAndStart = new Map<string, any>();

    appointments.forEach((appt: any) => {
      const apptIdRaw = appt?.id ?? appt?.appointmentId ?? appt?.appointment_id;
      const apptId = Number(apptIdRaw);
      if (!Number.isNaN(apptId)) {
        appointmentById.set(apptId, appt);
      }

      const apptInspectorId = Number(appt?.inspectorId ?? appt?.inspector_id);
      const apptStartRaw = appt?.date ?? appt?.startDate ?? appt?.planningAt;
      const apptStartMoment = apptStartRaw ? dayjs(apptStartRaw) : null;
      if (
        !Number.isNaN(apptInspectorId) &&
        apptInspectorId > 0 &&
        apptStartMoment &&
        apptStartMoment.isValid()
      ) {
        const key = `${apptInspectorId}-${apptStartMoment.valueOf()}`;
        if (!appointmentByInspectorAndStart.has(key)) {
          appointmentByInspectorAndStart.set(key, appt);
        }
      }
    });

    if (tasks.length && (attributesById?.size || 0) === 0) {
      return;
    }
    if (tasks.length && inspectorList.length === 0) {
      return;
    }

    const attributeQuantityMap = new Map<number, number>();
    (proposal?.attributes || []).forEach((pa: any) => {
      const attrId = Number(pa?.attributeId);
      if (!Number.isNaN(attrId)) {
        attributeQuantityMap.set(attrId, Number(pa?.quantity) || 0);
      }
    });

    const pickFirstDateValue = (source: any, keys: string[]): string | undefined => {
      if (!source || typeof source !== 'object') {
        return undefined;
      }
      for (const key of keys) {
        const value = source?.[key];
        if (value != null) {
          return value;
        }
      }
      return undefined;
    };

    const planEntries = tasks
      .map((task: any, index: number): PlanEntry | null => {
        const attributeId = Number(task?.attributeId);
        if (Number.isNaN(attributeId)) return null;
        const inspectorId = Number(task?.inspectorId);
        const qty = Number(task?.qty);
        const meta = attributesById.get(attributeId) || {};
        const inspector = inspectorById.get(inspectorId) || {};
        const firstName = inspector?.account?.firstName || '';
        const lastName = inspector?.account?.lastName || '';
        const inspectorNameRaw = `${firstName} ${lastName}`.trim();
        const inspectorName = inspectorNameRaw.length
          ? inspectorNameRaw
          : !Number.isNaN(inspectorId) && inspectorId > 0
            ? `#${inspectorId}`
            : '-';
        const appointmentIdRaw = task?.appointmentId ?? task?.inspectionAppointmentId ?? task?.inspectionAppointment?.id;
        const appointmentId = Number(appointmentIdRaw);
        const relatedAppointment = !Number.isNaN(appointmentId) ? appointmentById.get(appointmentId) : undefined;
        const rawTaskDate = pickFirstDateValue(task, ['date', 'startDate', 'planningAt', 'start', 'start_date', 'planning_at']);
        const rawAppointmentStart = pickFirstDateValue(
          relatedAppointment,
          ['startDate', 'date', 'planningAt', 'start', 'start_date', 'planning_at'],
        );
        const planningMomentCandidate = rawAppointmentStart ?? rawTaskDate;
        const planningMoment = planningMomentCandidate ? dayjs(planningMomentCandidate) : null;
        const formattedDate = planningMoment && planningMoment.isValid()
          ? planningMoment.format('DD/MM/YYYY HH:mm')
          : '-';

        return {
          id: `existing-${task.id ?? `${attributeId}-${index}`}`,
          taskId: Number(task.id) || undefined,
          appointmentId: Number(task.appointmentId) || undefined,
          taskDate: task.date,
          taskStatus: task.status,
          offeringKey: offeringKeyById[String(relatedAppointment?.offeringId)]
            ?? buildOfferingKey(meta?.offering?.id, meta?.offering?.name),
          paramKey: `${attributeId}`,
          paramName: meta.name || `Parametre #${attributeId}`,
          paramQuantity: attributeQuantityMap.get(attributeId),
          inspectorId: !Number.isNaN(inspectorId) ? inspectorId : undefined,
          inspectorName,
          date: formattedDate,
          count: !Number.isNaN(qty) ? qty : 0,
        };
      })
      .filter((entry: PlanEntry | null): entry is PlanEntry => entry !== null);

    const newOfferingDates: Record<string, DayRange[]> = {};
    const newOfferingInspectors: Record<string, (number | undefined)[]> = {};
    const newOfferingTransports: Record<string, (string | undefined)[]> = {};
    const newOfferingTransportNotes: Record<string, (string | undefined)[]> = {};
    const newOfferingAppointmentIds: Record<string, (number | undefined)[]> = {};
    const offeringSlotIndexMap = new Map<string, Map<string, number>>();
    const appointmentIdsWithSlots = new Set<number>();
    const appointmentOfferingMeta = new Map<number, { offeringKey: string; attributeId?: number }>();
    let fallbackSlotCounter = 0;

    tasks.forEach((task: any) => {
      const attributeId = Number(task?.attributeId);
      if (Number.isNaN(attributeId)) return;
      const meta = attributesById.get(attributeId) || {};
      const offeringId = coerceToNumber(meta?.offering?.id);
      const offeringName: string =
        typeof meta?.offering?.name === 'string' && meta.offering.name.trim().length > 0
          ? meta.offering.name.trim()
          : 'Diğer';
      let offeringKey = buildOfferingKey(offeringId, offeringName);
      const inspectorId = Number(task?.inspectorId);
      const appointmentIdRaw = task?.appointmentId ?? task?.inspectionAppointmentId ?? task?.inspectionAppointment?.id;
      const appointmentId = Number(appointmentIdRaw);
      let relatedAppointment: any | undefined;

      if (!Number.isNaN(appointmentId)) {
        relatedAppointment = appointmentById.get(appointmentId);
      }
      offeringKey = offeringKeyById[String(relatedAppointment?.offeringId)] ?? offeringKey;

      const taskStartRaw = pickFirstDateValue(task, ['date', 'startDate', 'planningAt', 'start', 'start_date', 'planning_at']);
      const taskEndRaw = pickFirstDateValue(task, ['endDate', 'planningEndDate', 'end', 'end_date', 'planning_end_date']);

      let startMomentValid = taskStartRaw ? dayjs(taskStartRaw) : null;
      if (startMomentValid && !startMomentValid.isValid()) {
        startMomentValid = null;
      }

      if (!relatedAppointment && !Number.isNaN(inspectorId) && inspectorId > 0 && startMomentValid) {
        const key = `${inspectorId}-${startMomentValid.valueOf()}`;
        relatedAppointment = appointmentByInspectorAndStart.get(key);
      }

      const appointmentStartRaw = pickFirstDateValue(
        relatedAppointment,
        ['startDate', 'date', 'planningAt', 'start', 'start_date', 'planning_at'],
      );
      const appointmentEndRaw = pickFirstDateValue(
        relatedAppointment,
        ['endDate', 'end', 'end_date', 'planningEndDate', 'planning_end_date'],
      );

      if (appointmentStartRaw) {
        const appointmentStartMoment = dayjs(appointmentStartRaw);
        if (appointmentStartMoment.isValid()) {
          startMomentValid = appointmentStartMoment;
        }
      }

      let endMomentValid: dayjs.Dayjs | null = taskEndRaw ? dayjs(taskEndRaw) : null;
      if (endMomentValid && !endMomentValid.isValid()) {
        endMomentValid = null;
      }
      if (appointmentEndRaw) {
        const appointmentEndMoment = dayjs(appointmentEndRaw);
        if (appointmentEndMoment.isValid()) {
          endMomentValid = appointmentEndMoment;
        }
      }

      if (startMomentValid && endMomentValid && !endMomentValid.isAfter(startMomentValid)) {
        endMomentValid = null;
      }

      if (startMomentValid && (!endMomentValid || !endMomentValid.isValid())) {
        endMomentValid =
          availabilityMode === 'DAILY'
            ? startMomentValid.add(1, 'day')
            : startMomentValid.add(1, 'hour');
      }

      if (!startMomentValid && appointmentStartRaw) {
        const appointmentStartMoment = dayjs(appointmentStartRaw);
        if (appointmentStartMoment.isValid()) {
          startMomentValid = appointmentStartMoment;
        }
      }
      if (!newOfferingDates[offeringKey]) {
        newOfferingDates[offeringKey] = [];
        newOfferingInspectors[offeringKey] = [];
        newOfferingTransports[offeringKey] = [];
        newOfferingTransportNotes[offeringKey] = [];
        newOfferingAppointmentIds[offeringKey] = [];
      }

      if (!offeringSlotIndexMap.has(offeringKey)) {
        offeringSlotIndexMap.set(offeringKey, new Map<string, number>());
      }

      const inspectorValue = !Number.isNaN(inspectorId) ? inspectorId : undefined;
      const transportRawFromTask =
        typeof task?.transportation === 'string'
          ? task.transportation
          : typeof task?.transportationType === 'string'
            ? task.transportationType
            : typeof task?.transportType === 'string'
              ? task.transportType
              : typeof task?.transport === 'string'
                ? task.transport
                : undefined;
      const transportRawFromAppointment = relatedAppointment
        ? typeof relatedAppointment.transportation === 'string'
          ? relatedAppointment.transportation
          : typeof relatedAppointment.transportationType === 'string'
            ? relatedAppointment.transportationType
            : typeof relatedAppointment.transportType === 'string'
              ? relatedAppointment.transportType
              : typeof relatedAppointment.transport === 'string'
                ? relatedAppointment.transport
                : undefined
        : undefined;
      const transportValue = normalizeTransportValue(transportRawFromTask ?? transportRawFromAppointment);
      const noteRawFromTask =
        typeof task?.transportationNote === 'string'
          ? task.transportationNote
          : typeof task?.note === 'string'
            ? task.note
            : undefined;
      const noteRawFromAppointment = relatedAppointment
        ? typeof relatedAppointment.transportationNote === 'string'
          ? relatedAppointment.transportationNote
          : typeof relatedAppointment.note === 'string'
            ? relatedAppointment.note
            : undefined
        : undefined;
      const normalizedNote = normalizeNoteValue(noteRawFromTask ?? noteRawFromAppointment);
      const resolvedAppointmentIdRaw =
        !Number.isNaN(appointmentId) && appointmentId > 0
          ? appointmentId
          : Number(relatedAppointment?.id);
      const resolvedAppointmentId =
        !Number.isNaN(resolvedAppointmentIdRaw) && resolvedAppointmentIdRaw > 0
          ? resolvedAppointmentIdRaw
          : undefined;
      const dayRange: DayRange = [startMomentValid, endMomentValid];
      const slotMap = offeringSlotIndexMap.get(offeringKey)!;
      const slotKey = (() => {
        if (resolvedAppointmentId != null) {
          return `appointment-${resolvedAppointmentId}`;
        }
        if (!Number.isNaN(inspectorId) && inspectorId > 0 && startMomentValid) {
          return `inspector-${inspectorId}-${startMomentValid.valueOf()}`;
        }
        return undefined;
      })();

      if (resolvedAppointmentId != null) {
        appointmentIdsWithSlots.add(resolvedAppointmentId);
        appointmentOfferingMeta.set(resolvedAppointmentId, {
          offeringKey,
          attributeId,
        });
      }

      if (slotKey && slotMap.has(slotKey)) {
        const targetIndex = slotMap.get(slotKey)!;
        newOfferingDates[offeringKey][targetIndex] = dayRange;
        newOfferingInspectors[offeringKey][targetIndex] = inspectorValue;
        newOfferingTransports[offeringKey][targetIndex] = transportValue;
        newOfferingTransportNotes[offeringKey][targetIndex] = normalizedNote;
        newOfferingAppointmentIds[offeringKey][targetIndex] = resolvedAppointmentId;
      } else {
        const targetIndex = newOfferingDates[offeringKey].length;
        const effectiveKey = slotKey ?? `auto-${offeringKey}-${fallbackSlotCounter++}`;
        slotMap.set(effectiveKey, targetIndex);
        newOfferingDates[offeringKey].push(dayRange);
        newOfferingInspectors[offeringKey].push(inspectorValue);
        newOfferingTransports[offeringKey].push(transportValue);
        newOfferingTransportNotes[offeringKey].push(normalizedNote);
        newOfferingAppointmentIds[offeringKey].push(resolvedAppointmentId);
      }
    });

    const summaryKeys = Object.keys(fieldControlSummary || {}).filter((key) => fieldControlSummary[key]);
    const fallbackOfferingKey = summaryKeys[0] || buildOfferingKey(null, 'Diğer');

    appointments.forEach((appointment: any) => {
      const appointmentId = Number(appointment?.id);
      if (Number.isNaN(appointmentId) || appointmentIdsWithSlots.has(appointmentId)) {
        return;
      }

      let startMoment = appointment?.startDate ? dayjs(appointment.startDate) : null;
      if (startMoment && !startMoment.isValid()) {
        startMoment = null;
      }
      if (!startMoment) {
        return;
      }

      let endMoment = appointment?.endDate ? dayjs(appointment.endDate) : null;
      if (endMoment && !endMoment.isValid()) {
        endMoment = null;
      }
      if (!endMoment || !endMoment.isValid() || !endMoment.isAfter(startMoment)) {
        endMoment =
          availabilityMode === 'DAILY'
            ? startMoment.add(1, 'day')
            : startMoment.add(1, 'hour');
      }

      const inspectorId = Number(appointment?.accountId);
      const inspectorValue = !Number.isNaN(inspectorId) ? inspectorId : undefined;
      const transportValue = normalizeTransportValue(appointment?.transportation);
      const noteValue = normalizeNoteValue(appointment?.note);
      const targetOfferingMeta = appointmentOfferingMeta.get(appointmentId);
      const appointmentOfferingId = coerceToNumber(
        appointment?.offeringId ?? appointment?.offering_id ?? appointment?.offering?.id,
      );
      const keyFromAppointmentId =
        appointmentOfferingId != null ? offeringKeyById[String(appointmentOfferingId)] : undefined;
      const targetOfferingKey =
        keyFromAppointmentId ?? targetOfferingMeta?.offeringKey ?? fallbackOfferingKey;

      if (!newOfferingDates[targetOfferingKey]) {
        newOfferingDates[targetOfferingKey] = [];
        newOfferingInspectors[targetOfferingKey] = [];
        newOfferingTransports[targetOfferingKey] = [];
        newOfferingTransportNotes[targetOfferingKey] = [];
        newOfferingAppointmentIds[targetOfferingKey] = [];
      }
      if (!offeringSlotIndexMap.has(targetOfferingKey)) {
        offeringSlotIndexMap.set(targetOfferingKey, new Map<string, number>());
      }
      const slotMap = offeringSlotIndexMap.get(targetOfferingKey)!;
      const slotKey = `appointment-${appointmentId}`;
      if (slotMap.has(slotKey)) {
        return;
      }
      const targetIndex = newOfferingDates[targetOfferingKey].length;
      slotMap.set(slotKey, targetIndex);
      appointmentIdsWithSlots.add(appointmentId);
      newOfferingDates[targetOfferingKey].push([startMoment, endMoment]);
      newOfferingInspectors[targetOfferingKey].push(inspectorValue);
      newOfferingTransports[targetOfferingKey].push(transportValue);
      newOfferingTransportNotes[targetOfferingKey].push(noteValue);
      newOfferingAppointmentIds[targetOfferingKey].push(appointmentId);
    });

    const sortOfferingEntries = (offeringName: string) => {
      const dates = newOfferingDates[offeringName];
      if (!dates || dates.length <= 1) {
        return;
      }
      const indices = dates.map((_, idx) => idx);
      const toEpoch = (value?: dayjs.Dayjs | null) =>
        value && value.isValid() ? value.valueOf() : Number.MAX_SAFE_INTEGER;
      indices.sort((a, b) => {
        const first = toEpoch(dates[a]?.[0]);
        const second = toEpoch(dates[b]?.[0]);
        if (first !== second) {
          return first - second;
        }
        const firstEnd = toEpoch(dates[a]?.[1]);
        const secondEnd = toEpoch(dates[b]?.[1]);
        return firstEnd - secondEnd;
      });
      const alreadySorted = indices.every((index, position) => index === position);
      if (alreadySorted) {
        return;
      }
      const reorder = <T,>(list?: (T | undefined)[]) => (list ? indices.map((idx) => list[idx]) : []);
      newOfferingDates[offeringName] = reorder(dates) as DayRange[];
      newOfferingInspectors[offeringName] = reorder(newOfferingInspectors[offeringName]);
      newOfferingTransports[offeringName] = reorder(newOfferingTransports[offeringName]);
      newOfferingTransportNotes[offeringName] = reorder(newOfferingTransportNotes[offeringName]);
      newOfferingAppointmentIds[offeringName] = reorder(newOfferingAppointmentIds[offeringName]);
    };

    Object.keys(newOfferingDates).forEach(sortOfferingEntries);

    setPagePlans(planEntries);
    setOfferingDates(newOfferingDates);
    setOfferingInspectors(newOfferingInspectors);
    const preservedRecord: Record<string, number[]> = {};
    Object.entries(newOfferingInspectors).forEach(([offering, list]) => {
      const unique = Array.from(
        new Set(
          (list || [])
            .map((value) => Number(value))
            .filter((value) => !Number.isNaN(value) && value > 0),
        ),
      );
      if (unique.length > 0) {
        preservedRecord[offering] = unique;
      }
    });
    setPreservedInspectorIdsByOffering(preservedRecord);
    setOfferingTransports(newOfferingTransports);
    setOfferingTransportNotes(newOfferingTransportNotes);
    setOfferingAppointmentIds(newOfferingAppointmentIds);
    setFieldControlDays((prev) => {
      const next = { ...prev } as Record<string, number>;
      offeringSlotIndexMap.forEach((slotMap, offering) => {
        const required = slotMap.size;
        if (!next[offering] || next[offering] < required) {
          next[offering] = required;
        }
      });
      return next;
    });

    setInitialPlanApplied(true);
  }, [
    existingPlan,
    initialPlanApplied,
    attributesById,
    inspectorById,
    inspectorList.length,
    offeringKeyById,
    proposal,
    availabilityMode,
    fieldControlSummary,
  ]);

  // Efor (dakika) hesabı: Parametre kartındaki seçimlere göre uzman x offering toplamı
  const effortByInspector = useMemo(() => {
    const map = new Map<number, Record<string, number>>();
    (selectedParams || []).forEach((param: any) => {
      const attrId = Number(param?.key);
      if (Number.isNaN(attrId) || attrId <= 0) {
        return;
      }
      const meta = attributesById.get(attrId) || {};
      const minutesPer = Number(meta?.fieldControlTime) || 0;
      const offName: string =
        typeof meta?.offering?.name === 'string' && meta.offering.name.trim().length > 0
          ? meta.offering.name.trim()
          : 'Diğer';
      const key = buildOfferingKey(meta?.offering?.id, offName);
      const allocations = parameterInspectorSelections?.[attrId] || [];
      allocations.forEach((allocation) => {
        const uid = Number(allocation?.inspectorId);
        if (Number.isNaN(uid) || uid <= 0) {
          return;
        }
        const add = (Number(allocation?.count) || 0) * minutesPer;
        if (add <= 0) {
          return;
        }
        const rec = map.get(uid) || {};
        rec[key] = (rec[key] || 0) + add;
        map.set(uid, rec);
      });
    });
    return map;
  }, [selectedParams, parameterInspectorSelections, attributesById]);

  const planModalInspectorOptions = useMemo(() => {
    const selectedAccountIds = new Set<number>();
    Object.values(offeringInspectors || {}).forEach((list) => {
      (list || []).forEach((val) => {
        const id = Number(val);
        if (!Number.isNaN(id) && id > 0) {
          selectedAccountIds.add(id);
        }
      });
    });
    (pagePlans || []).forEach((pl: any) => {
      const id = Number(pl?.inspectorId);
      if (!Number.isNaN(id) && id > 0) {
        selectedAccountIds.add(id);
      }
    });
    Object.values(preservedInspectorIdsByOffering || {}).forEach((list) => {
      (list || []).forEach((val) => {
        const id = Number(val);
        if (!Number.isNaN(id) && id > 0) {
          selectedAccountIds.add(id);
        }
      });
    });
    const attrRequirement = (() => {
      if (!planTarget) {
        return undefined;
      }
      const attrId = Number((planTarget as any)?.key);
      if (Number.isNaN(attrId)) {
        return undefined;
      }
      return proposalAttributeRequirements.get(attrId);
    })();
    let fallback: any[] | undefined;
    if (planTarget) {
      const attrId = Number((planTarget as any)?.key);
      if (!Number.isNaN(attrId)) {
        const eligible = eligibleInspectorsByAttributeId.get(attrId);
        if (Array.isArray(eligible) && eligible.length > 0) {
          fallback = eligible;
        }
      }
    }
    if (!fallback || fallback.length === 0) {
      fallback = inspectorList;
    }
    const requirementFiltered = attrRequirement
      ? filterInspectorsByRequirementSets(fallback, [attrRequirement])
      : [...fallback];
    const activeInspectors = requirementFiltered.filter((inspector: any) => isAccountActive(inspector));

    const mergedById = new Map<number, any>();
    activeInspectors.forEach((inspector: any) => {
      const accountId = Number(inspector?.account?.id ?? inspector?.id);
      if (!Number.isNaN(accountId) && accountId > 0) {
        mergedById.set(accountId, inspector);
      }
    });

    selectedAccountIds.forEach((accountId) => {
      if (Number.isNaN(accountId) || accountId <= 0 || mergedById.has(accountId)) {
        return;
      }
      const inspector =
        inspectorById.get(accountId) ||
        requirementFiltered.find((ins: any) => Number(ins?.account?.id ?? ins?.id) === accountId) ||
        inspectorList.find((ins: any) => Number(ins?.account?.id ?? ins?.id) === accountId);
      if (!inspector) {
        return;
      }
      if (attrRequirement && !isInspectorEligibleForRequirement(inspector, attrRequirement)) {
        return;
      }
      mergedById.set(accountId, inspector);
    });

    const nameOf = (ins: any) =>
      `${(ins?.account?.firstName || '').toString()} ${(ins?.account?.lastName || '').toString()}`.trim();
    return Array.from(mergedById.values()).sort((a, b) => nameOf(a).localeCompare(nameOf(b), 'tr'));
  }, [
    planTarget,
    eligibleInspectorsByAttributeId,
    offeringInspectors,
    preservedInspectorIdsByOffering,
    pagePlans,
    inspectorById,
    inspectorList,
    proposalAttributeRequirements,
  ]);

  const minutesToDays = (minutes: number) => {
    if (!minutes || minutes <= 0) return '0.00';
    const days = minutes / 480; // 1 iş günü = 480 dk
    return days.toFixed(2);
  };

  const openPlanModal = (record: any) => {
    if (isReadOnly) {
      return;
    }
    setPlanTarget(record);
    setPlanModalOpen(true);
    setSumExceeds(false);
    const existing = pagePlans.filter((p) => p.paramKey === record.key);
    if (existing.length > 0) {
      setEditingExisting(true);
      planForm.setFieldsValue({
        plans: existing.map((pl) => ({ inspectorId: pl.inspectorId, count: pl.count })),
      });
    } else {
      setEditingExisting(false);
      // Varsayılan olarak adet = parametredeki adet
      planForm.setFieldsValue({ plans: [{ inspectorId: undefined, count: record?.quantity || 1 }] });
    }
  };


  const onPlansChange = () => {
    const vals = planForm.getFieldValue('plans') || [];
    const sum = vals.reduce((acc: number, cur: any) => acc + (Number(cur?.count) || 0), 0);
    const maxQty = planTarget?.quantity || 0;
    // Halihazırda sayfada planlanmış olan toplamı da dahil et, ancak düzenleme modunda double count yapma
    const alreadyPlanned = editingExisting
      ? 0
      : pagePlans
        .filter((p) => p.paramKey === planTarget?.key)
        .reduce((acc, cur) => acc + (Number(cur.count) || 0), 0);
    setSumExceeds(sum + alreadyPlanned > maxQty);
  };

  const submitPlans = async () => {
    try {
      const values = await planForm.validateFields();
      const sum = (values.plans || []).reduce((acc: number, cur: any) => acc + (Number(cur?.count) || 0), 0);
      const maxQty = planTarget?.quantity || 0;
      const alreadyPlanned = editingExisting
        ? 0
        : pagePlans
          .filter((p) => p.paramKey === planTarget?.key)
          .reduce((acc, cur) => acc + (Number(cur.count) || 0), 0);
      if (sum + alreadyPlanned > maxQty) {
        setSumExceeds(true);
        openNotification('error', 'Hata', 'Toplam adet, parametredeki adeti geçemez.');
        return;
      }
      // Formdaki satırları sayfa plan listesine ekle
      const inspectorName = (accountId: number) => {
        const inspector = inspectorById.get(accountId);
        if (!inspector) {
          return `#${accountId}`;
        }
        const first = (inspector?.account?.firstName || '').toString();
        const last = (inspector?.account?.lastName || '').toString();
        const fullName = `${first} ${last}`.trim();
        return fullName.length > 0 ? fullName : `#${accountId}`;
      };
      const newItems = (values.plans || []).map((pl: any) => ({
        id: `${planTarget.key}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        paramKey: planTarget.key,
        paramName: planTarget.name,
        paramQuantity: planTarget.quantity,
        inspectorId: pl.inspectorId,
        inspectorName: inspectorName(pl.inspectorId),
        date: pl.date?.format ? pl.date.format('DD/MM/YYYY HH:mm') : pl.date,
        count: Number(pl.count) || 0,
        // contractId removed
      }));
      setPagePlans((prev) => {
        const without = prev.filter((p) => p.paramKey !== planTarget.key);
        return [...without, ...newItems];
      });
      openNotification('success', 'Başarılı', 'Plan listeye eklendi.');
      setPlanModalOpen(false);
      setPlanTarget(null);
      setEditingExisting(false);
    } catch (e) {
      // validation errors ignored
    }
  };

  const parameterCardColumns: ColumnsType<any> = [
    {
      title: 'Parametre',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => (
        <span
          style={{
            maxWidth: 400,
            display: 'inline-block',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={text}
        >
          {text}
        </span>
      ),
    },
    {
      title: 'Adet',
      key: 'quantity',
      width: 140,
      render: (_: any, record: any) => {
        const attributeId = Number(record?.key);
        const requiredQuantity = Math.max(0, Number(record?.quantity) || 0);
        const allocations = !Number.isNaN(attributeId) && attributeId > 0
          ? (parameterInspectorSelections[attributeId] || [])
          : [];
        const plannedQuantity = allocations.reduce((acc, allocation) => acc + (Number(allocation?.count) || 0), 0);
        const isCompleted = requiredQuantity > 0 && plannedQuantity === requiredQuantity;
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
            <span>{plannedQuantity}/{requiredQuantity}</span>
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: isCompleted ? '#52c41a' : '#ff4d4f',
                display: 'inline-block',
              }}
            />
          </span>
        );
      },
    },
    {
      title: 'PKM Uzmanı',
      key: 'pkmInspector',
      width: 350,
      render: (_: any, record: any) => {
        const attributeId = Number(record?.key);
        if (Number.isNaN(attributeId) || attributeId <= 0) {
          return <Text type='secondary'>-</Text>;
        }
        const options = matchedDraftInspectorOptionsByAttributeId.get(attributeId) || [];
        const quantity = Number(record?.quantity) || 0;
        const allowMultiple = quantity > 1 && options.length > 1;
        const maxSelection = allowMultiple ? Math.max(1, quantity) : 1;
        const safeQuantity = Math.max(1, quantity);
        const allocations = parameterInspectorSelections[attributeId] || [];
        const selectedValues = allocations.map((allocation) => allocation.inspectorId);
        const value = allowMultiple ? selectedValues : selectedValues[0];
        const optionLabelByValue = new Map(options.map((option) => [option.value, option.label]));
        return (
          <div>
            <Select
              allowClear
              optionFilterProp='label'
              style={{ width: '100%' }}
              mode={allowMultiple ? 'multiple' : undefined}
              maxCount={allowMultiple ? maxSelection : undefined}
              maxTagCount={allowMultiple ? 'responsive' : undefined}
              placeholder={
                options.length > 0
                  ? allowMultiple
                    ? `PKM Uzmanları Seç (en fazla ${maxSelection})`
                    : 'PKM Uzmanı Seç'
                  : 'Eşleşen PKM yok'
              }
              options={options}
              value={value}
              onChange={(nextValue: any) =>
                handleParameterInspectorSelectChange(attributeId, nextValue, maxSelection, safeQuantity)}
              disabled={isReadOnly || options.length === 0}
            />

            {allocations.length > 0 ? (
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {allocations.map((allocation) => {
                  const inspectorId = Number(allocation?.inspectorId);
                  const count = Number(allocation?.count) || 1;
                  const label = optionLabelByValue.get(inspectorId) || `#${inspectorId}`;
                  const otherTotal = allocations
                    .filter((entry) => Number(entry?.inspectorId) !== inspectorId)
                    .reduce((acc, entry) => acc + (Number(entry?.count) || 0), 0);
                  const maxForInspector = Math.max(1, safeQuantity - otherTotal);

                  return (
                    <div key={`${attributeId}-${inspectorId}`} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Text style={{ fontSize: 12, minWidth: 130 }}>{label}</Text>
                      <InputNumber
                        size='small'
                        min={1}
                        max={maxForInspector}
                        value={count}
                        onChange={(nextValue) =>
                          handleParameterInspectorCountChange(
                            attributeId,
                            inspectorId,
                            nextValue,
                            maxSelection,
                            safeQuantity,
                          )}
                        disabled={isReadOnly}
                      />
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        );
      },
    },
  ];

  return (
    <div className='mb-60px'>
      <Row justify='space-between' align='middle' style={{ marginBottom: 16 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            {proposal ? `Teklif No: ${proposal.proposalNumber || '-'}` : 'Teklif bilgisi yükleniyor...'}
          </Title>
          {proposal && (proposal.requestedPlanningStartDate || proposal.requestedPlanningEndDate) && (
            <Text type='secondary' style={{ fontSize: 13 }}>
              Planlama Talep: {proposal.requestedPlanningStartDate ? formatDate(proposal.requestedPlanningStartDate) : '-'}
              {proposal.requestedPlanningEndDate ? ` - ${formatDate(proposal.requestedPlanningEndDate)}` : ''}
            </Text>
          )}
        </div>
        <Space>

          <Button onClick={() => navigate(-1)} className='d-button action-button'>Geri</Button>
        </Space>
      </Row>

      {isReadOnly && !loading ? (
        <Alert
          type='info'
          showIcon
          message='Planlama sadece görüntülenebilir.'
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {pendingDeletedAppointmentId != null && !deletingAppointment ? (
        <Alert type='warning' showIcon message='Randevu silindi. Görev bağlantılarını güncellemek için planı yenileyiniz.'
          action={<Button onClick={retryDeletedAppointmentRefresh}>Planı Yenile</Button>} style={{ marginBottom: 16 }} />
      ) : null}

      <Collapse
        accordion
        defaultActiveKey='inspector-plans'
        className='planning-detail-accordion mt-16px'
        items={[
          {
            key: 'address',
            label: 'Firma / Adres Bilgileri',
            children: (
              <div className='planning-accordion-content'>
                <ControlAddressCard
                  className='br-30px'
                  proposalId={proposal?.id ?? (id ? Number(id) : undefined)}
                  proposal={proposal}
                  fetchOnMount={false}
                  onProposalLoaded={setProposal}
                  loading={loading}
                />
              </div>
            ),
          },
          {
            key: 'authorized',
            label: 'Yetkili Kişiler',
            children: (
              <div className='planning-accordion-content'>
                <PlanAuthorizedPersonsCard
                  loading={loading}
                  isReadOnly={isReadOnly}
                  companyId={proposal?.company?.id ? Number(proposal.company.id) : undefined}
                  planAuthorizedPersonIds={planAuthorizedPersonIds}
                  setPlanAuthorizedPersonIds={setPlanAuthorizedPersonIds}
                />
              </div>
            ),
          },
          {
            key: 'inspector-plans',
            label: 'PKM Uzmanı Planlaması',
            extra: (
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 12 }}>
                <Button
                  size='small'
                  onClick={(event) => {
                    event.stopPropagation();
                    setDailySelectedInspectorsModalOpen(true);
                  }}
                >
                  Plan Görünümü
                </Button>
                <Text style={{ color: '#000' }}>
                  <span style={{ fontWeight: 700 }}>Makine:</span> {machineElectricHygieneSummary.machine.ceil} ({machineElectricHygieneSummary.machine.exact})
                </Text>
                <Text style={{ color: '#000' }}>
                  <span style={{ fontWeight: 700 }}>Elektrik:</span> {machineElectricHygieneSummary.electric.ceil} ({machineElectricHygieneSummary.electric.exact})
                </Text>
                <Text style={{ color: '#000' }}>
                  <span style={{ fontWeight: 700 }}>İş Hijyeni:</span> {machineElectricHygieneSummary.hygiene.ceil} ({machineElectricHygieneSummary.hygiene.exact})
                </Text>
              </div>
            ),
            children: (
              <div className='planning-accordion-content'>
                <InspectorPlanningCard
                  proposalId={proposal?.id ?? (id ? Number(id) : undefined)}
                  loading={loading}
                  isReadOnly={isReadOnly || updatingAppointment || deletingAppointment || savingPlanStatus !== null || pendingDeletedAppointmentId != null}
                  inspectors={inspectorList}
                  onInspectorsLoaded={handlePlanningContextInspectorsLoaded}
                  offeringDates={offeringDates}
                  offeringInspectors={offeringInspectors}
                  offeringAppointmentIds={offeringAppointmentIds}
                  offeringTransports={offeringTransports}
                  offeringTransportNotes={offeringTransportNotes}
                  offeringLabels={offeringLabelsByKey}
                  publicHolidays={publicHolidays}
                  parameterOptions={parameterOptions}
                  plannedQuantityByAttributeId={plannedQuantityByAttributeId}
                  draftParametersBySlot={draftParametersBySlot}
                  onCreatePlan={handleCreatePlanFromInspectorCard}
                  onUpdatePagePlan={handleUpdatePagePlanFromInspectorCard}
                  onDeletePagePlan={handleDeletePagePlanFromInspectorCard}
                />
              </div>
            ),
          },
          {
            key: 'proposal-parameters',
            label: 'Parametreler',
            children: (
              <div className='planning-accordion-content'>
                <ParameterCard
                  loading={loading}
                  dataSource={selectedParams}
                  columns={parameterCardColumns}
                />
              </div>
            ),
          },
          {
            key: 'summary',
            label: 'Kategori Özeti',
            children: (
              <div className='planning-accordion-content'>
                <CategorySummaryCard
                  loading={loading}
                  groupPivot={groupPivot}
                  inspectorById={inspectorById}
                  effortByInspector={effortByInspector}
                  minutesToDays={minutesToDays}
                />
              </div>
            ),
          }
        ]}
      />


      {!isReadOnly && (
        <Row justify={"end"} align={'middle'} gutter={[16, 16]} className='mt-24px'>
          <Col>
            <Popconfirm
              title='Planı kaydetmek istediğinizden emin misiniz?'
              okText='Evet'
              cancelText='İptal'
              onConfirm={() => handleSavePlan('PLANNED')}
              disabled={savingPlanStatus !== null || updatingAppointment || deletingAppointment || pendingDeletedAppointmentId != null}
              okButtonProps={{ loading: savingPlanStatus === 'PLANNED', disabled: updatingAppointment || deletingAppointment || pendingDeletedAppointmentId != null }}
              cancelButtonProps={{ disabled: savingPlanStatus !== null }}
            >
              <Button
                type='primary'
                className='d-button dark-button'
                loading={savingPlanStatus === 'PLANNED'}
                disabled={savingPlanStatus !== null || updatingAppointment || deletingAppointment || pendingDeletedAppointmentId != null}
              >
                Planı Tamamla
              </Button>
            </Popconfirm>
          </Col>
        </Row>
      )}

      <Modal
        title={planTarget ? `Planla — ${planTarget.name}` : 'Planla'}
        open={planModalOpen && !isReadOnly}
        onCancel={() => setPlanModalOpen(false)}
        footer={null}
        width={900}
        destroyOnClose
      >
        <Form form={planForm} layout='vertical' onValuesChange={onPlansChange} disabled={isReadOnly}>
          {planTarget && (
            <div style={{ marginBottom: 8 }}>
              <Text type='secondary'>Toplam Adet: {planTarget.quantity}</Text>
            </div>
          )}
          {sumExceeds && (
            <Alert type='error' showIcon message='Toplam adet, parametredeki adeti geçemez.' style={{ marginBottom: 12 }} />
          )}

          <Form.List name='plans'>
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Row key={key} gutter={8} align='middle' style={{ marginBottom: 8 }}>
                    {/* PKM */}
                    <Col span={8}>
                      <Form.Item
                        {...restField}
                        name={[name, 'inspectorId']}
                        label='P.K.M Uzmanı'
                        rules={[
                          { required: true, message: 'Lütfen uzman seçiniz' },
                          () => ({
                            validator(_, value) {
                              const plans = planForm.getFieldValue('plans') || [];
                              const currentIndex = name as number;
                              if (!value) return Promise.resolve();
                              const duplicate = plans.some((p: any, idx: number) => idx !== currentIndex && p?.inspectorId === value);
                              return duplicate ? Promise.reject(new Error('Aynı P.K.M Uzmanı birden fazla seçilemez')) : Promise.resolve();
                            },
                          }),
                        ]}
                      >
                        <Select
                          className='planning-pkm-select'
                          showSearch
                          optionFilterProp='children'
                          placeholder='Seçiniz'
                          disabled={isReadOnly}
                        >
                          {planModalInspectorOptions.map((ins: any) => {
                            const optionId = Number(ins?.account?.id ?? ins?.id);
                            if (Number.isNaN(optionId) || optionId <= 0) {
                              return null;
                            }
                            const currentPlans = planForm.getFieldValue('plans') || [];
                            const currentIndex = name as number;
                            const currentVal = Number(currentPlans?.[currentIndex]?.inspectorId);
                            const selectedIds = currentPlans
                              .map((p: any, idx: number) => (idx === currentIndex ? undefined : p?.inspectorId))
                              .map((v: any) => Number(v))
                              .filter((v: number) => !Number.isNaN(v) && v > 0);
                            const disabled = selectedIds.includes(optionId) && optionId !== currentVal;
                            return (
                              <Select.Option key={optionId} value={optionId} disabled={disabled}>
                                {(ins?.account?.firstName || '').toString()} {(ins?.account?.lastName || '').toString()}
                              </Select.Option>
                            );
                          })}
                        </Select>
                      </Form.Item>
                    </Col>
                    {/* Tarih ve sözleşme alanları kaldırıldı */}
                    {/* Adet */}
                    <Col span={3}>
                      <Form.Item
                        {...restField}
                        name={[name, 'count']}
                        label='Adet'
                        rules={[{ required: true, message: 'Adet giriniz' }]}
                      >
                        <InputNumber min={1} max={computeRowMax(name)} style={{ width: '100%' }} disabled={isReadOnly} />
                      </Form.Item>
                    </Col>
                    {/* Sil */}
                    <Col span={1} style={{ display: 'flex', alignItems: 'end' }}>
                      <Button danger type='link' onClick={() => !isReadOnly && remove(name)} disabled={isReadOnly}>Sil</Button>
                    </Col>
                  </Row>
                ))}
                <Button
                  type='dashed'
                  onClick={() => {
                    if (isReadOnly) return;
                    const vals = planForm.getFieldValue('plans') || [];
                    const used = vals.reduce((acc: number, cur: any) => acc + (Number(cur?.count) || 0), 0);
                    const maxQty = planTarget?.quantity || 0;
                    if (maxQty === 1 || fields.length >= maxQty) return; // tek adet veya limite ulaştıysa ikinci satırı engelle
                    const remaining = Math.max(maxQty - used, 0);
                    add({ inspectorId: undefined, date: undefined, count: remaining > 0 ? remaining : 1 });
                  }}
                  disabled={
                    isReadOnly || (planTarget?.quantity || 0) === 1 || fields.length >= (planTarget?.quantity || 0)
                  }
                  block
                >
                  Satır Ekle
                </Button>
              </>
            )}
          </Form.List>
          <div style={{ textAlign: 'right', marginTop: 12 }}>
            <Button
              type='primary'
              className='d-button dark-button'
              onClick={submitPlans}
              disabled={isReadOnly || sumExceeds}
            >
              Planla
            </Button>
          </div>
        </Form>
      </Modal>

      <Modal
        title='Plan Görünümü'
        open={dailySelectedInspectorsModalOpen}
        onCancel={() => setDailySelectedInspectorsModalOpen(false)}
        footer={null}
        width={760}
        destroyOnClose
      >
        {dailySelectedInspectors.length === 0 ? (
          <Text type='secondary'>Henüz planlanan uzman bulunmuyor.</Text>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {dailySelectedInspectors.map((dayGroup) => (
              <div
                key={dayGroup.dayKey}
                style={{ border: '1px solid #f0f0f0', borderRadius: 10, padding: '12px 14px', background: '#fff' }}
              >
                <div style={{ fontWeight: 700, marginBottom: 8 }}>{dayGroup.dayLabel}</div>
                {dayGroup.inspectors.map((inspector) => (
                  <div key={`${dayGroup.dayKey}-${inspector.id}`} style={{ marginBottom: 8 }}>
                    <div style={{ fontWeight: 600 }}>
                      {inspector.name}
                      {inspector.identityNumber ? (
                        <span style={{ fontWeight: 500, color: '#595959' }}>{` - TCKN: ${inspector.identityNumber}`}</span>
                      ) : null}
                    </div>
                    {inspector.slots.length > 0 ? (
                      <div style={{ fontSize: 12, color: '#8c8c8c', display: 'flex', flexDirection: 'column', gap: 2, marginTop: 2 }}>
                        {inspector.slots.map((slot: any) => (
                          <span key={slot.key}>
                            Ulaşım Türü: {slot.transportLabel} | Saat: {slot.timeLabel}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2 }}>
                        Ulaşım Türü: - | Saat: -
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PlanningDetail;
